import { PLAN_KEYS, canAccess, getPlanConfig } from './shared/plan';
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ZAxis,
  Cell
} from "recharts";
import EcovantaPricingPage from "./components/EcovantaPricingPage";
import HelpTooltip from "./components/HelpTooltip";
import { HELP } from "./helpContent";
import EcovantaLandingPage from "./EcovantaLandingPage";
import { calculateBig4ESGScore } from "./esgScoring";
import {
  PageContainer,
  Navbar,
  Section,
  DashboardGrid,
  TwoColumnLayout
} from "./components/layout";
import { Card, Button, Badge, Input, Textarea, Select } from "./components/ui";
import SidebarLayout from "./components/SidebarLayout";
import SubscriptionPage from "./components/SubscriptionPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import {
  initAnalytics,
  identifyUser,
  trackEvent
} from "./analytics";
import ESGMaturityBenchmark from "./pages/ESGMaturityBenchmark";
import ESGMaturityAssessmentHistory from "./pages/ESGMaturityAssessmentHistory";
import ESGMaturityAssessmentDetail from "./pages/ESGMaturityAssessmentDetail";
import ESGGapAnalysis from "./pages/ESGGapAnalysis";
import RequestAccessPage from "./pages/RequestAccessPage";
import ESGRoadmapPreview from "./pages/ESGRoadmapPreview";
import LeadManagementDashboard from "./pages/LeadManagementDashboard";
import FollowUpDashboard from "./pages/FollowUpDashboard";
import AdvisoryDocumentsPage from "./pages/AdvisoryDocumentsPage";
import CustomerDashboardPage from "./pages/CustomerDashboardPage";
import ExecutiveDashboardPage from "./pages/ExecutiveDashboardPage";
import DocumentIntelligencePage from "./pages/DocumentIntelligencePage";
import "./styles/design-system.css";
import { UserProvider } from "./contexts/UserContext";
import UpgradePage from "./pages/UpgradePage";
import EvidenceLibraryPage from "./pages/EvidenceLibraryPage";
import { addNotification, addNotificationOnce } from "./services/notificationService";
import API from "./config/api";
import CopilotWorkspacePage from "./pages/CopilotWorkspacePage";

const defaultMaterialityTopic = {
  topicCode: "E1",
  topicLabel: "Climate change",
  impactMateriality: {
    severity: 3,
    scale: 3,
    scope: 3,
    irremediability: 3,
    likelihood: 3
  },
  financialMateriality: {
    magnitude: 3,
    likelihood: 3,
    timeHorizon: "medium"
  },
  stakeholdersConsulted: "",
  isMaterial: true,
  rationale: "",
  impactScore100: 0,
  financialScore100: 0,
  overallMaterialityScore: 0
};

const initialReportForm = {
  companyName: "",
  sector: "tech",
  reportingYear: new Date().getFullYear(),
  esrs2: {
    governance: "",
    strategy: "",
    impactsRisksOpportunities: "",
    metricsTargets: ""
  },
  e1: {
    scope1Emissions: 0,
    scope2Emissions: 0,
    scope3Emissions: 0,
    climatePolicies: ""
  },
  s1: {
    workforcePolicies: "",
    diversityInclusion: ""
  },
  g1: {
    antiCorruption: "",
    whistleblowing: ""
  },
  aiDraft: {
    executiveSummary: "",
    disclosureDraft: "",
    dataGaps: "",
    recommendations: ""
  },
  scorecard: {
    benchmark: 0,
    overallScore: 0
  },
  reviewStatus: "draft",
  materialityTopics: [{ ...defaultMaterialityTopic }]
};

const DEFAULT_SECTOR_BENCHMARKS = {
  tech: { sectorAverage: 65, topQuartile: 82 },
  energy: { sectorAverage: 58, topQuartile: 78 },
  manufacturing: { sectorAverage: 61, topQuartile: 79 }
};

const getTimeHorizonScore = (timeHorizon) => {
  if (timeHorizon === "short") return 5;
  if (timeHorizon === "medium") return 3;
  if (timeHorizon === "long") return 2;
  return 3;
};

const calculateMaterialityScores = (topic) => {
  const impact = topic?.impactMateriality || {};
  const financial = topic?.financialMateriality || {};

  const impactRaw =
    Number(impact.severity || 0) * 0.3 +
    Number(impact.scale || 0) * 0.2 +
    Number(impact.scope || 0) * 0.2 +
    Number(impact.irremediability || 0) * 0.15 +
    Number(impact.likelihood || 0) * 0.15;

  const financialRaw =
    Number(financial.magnitude || 0) * 0.5 +
    Number(financial.likelihood || 0) * 0.3 +
    getTimeHorizonScore(financial.timeHorizon) * 0.2;

  const impactScore100 = Math.round((impactRaw / 5) * 100);
  const financialScore100 = Math.round((financialRaw / 5) * 100);
  const overallMaterialityScore = Math.max(
    impactScore100,
    financialScore100
  );
  const isMaterial = overallMaterialityScore >= 60;

  return {
    impactScore100,
    financialScore100,
    overallMaterialityScore,
    isMaterial
  };
};

const calculateOverallESGScore = (form) => {
  const eScores = [
    form.e1?.scope1Emissions > 0 ? 3 : 1,
    form.e1?.scope2Emissions > 0 ? 3 : 1,
    form.e1?.scope3Emissions > 0 ? 3 : 1,
    form.e1?.climatePolicies ? 4 : 1
  ];

  const sScores = [
    form.s1?.workforcePolicies ? 4 : 1,
    form.s1?.diversityInclusion ? 4 : 1
  ];

  const gScores = [
    form.g1?.antiCorruption ? 4 : 1,
    form.g1?.whistleblowing ? 4 : 1
  ];

  const allScores = [...eScores, ...sScores, ...gScores];
  const avg5 =
    allScores.reduce((sum, value) => sum + value, 0) / allScores.length;

  return Math.round((avg5 / 5) * 100);
};

const getBenchmarkComparisonData = (reportForm, analytics) => {
  const sector = reportForm?.sector || "tech";

  const sectorBenchmarks =
    DEFAULT_SECTOR_BENCHMARKS[sector] || DEFAULT_SECTOR_BENCHMARKS.tech;

  const companyScore = Number(
    reportForm?.scorecard?.overallScore ||
      analytics?.averageScore ||
      0
  );

  return [
    {
      name: "Your Company",
      score: companyScore,
      value: companyScore,
    },
    {
      name: "Sector Average",
      score: sectorBenchmarks.sectorAverage,
      value: sectorBenchmarks.sectorAverage,
    },
    {
      name: "Top Quartile",
      score: sectorBenchmarks.topQuartile,
      value: sectorBenchmarks.topQuartile,
    },
  ];
};

const getMaterialityHeatmapData = (materialityTopics = []) => {
  return materialityTopics.map((topic, index) => ({
    x: Number(topic.financialScore100 || 0),
    y: Number(topic.impactScore100 || 0),
    z: 100,
    name: topic.topicCode || `Topic ${index + 1}`,
    label: topic.topicLabel || "",
    overall: Number(topic.overallMaterialityScore || 0),
    result: topic.isMaterial ? "Material" : "Not Material"
  }));
};

const getComplianceGapData = (reportForm) => {
  const e1Missing = [];
  if (!reportForm?.e1?.scope1Emissions) e1Missing.push("Scope 1 emissions");
  if (!reportForm?.e1?.scope2Emissions) e1Missing.push("Scope 2 emissions");
  if (!reportForm?.e1?.scope3Emissions) e1Missing.push("Scope 3 emissions");
  if (!reportForm?.e1?.climatePolicies?.trim()) {
    e1Missing.push("Climate policies");
  }

  const esrs2Missing = [];
  if (!reportForm?.esrs2?.governance?.trim()) {
    esrs2Missing.push("Governance");
  }
  if (!reportForm?.esrs2?.strategy?.trim()) {
    esrs2Missing.push("Strategy");
  }
  if (!reportForm?.esrs2?.impactsRisksOpportunities?.trim()) {
    esrs2Missing.push("Impacts, risks and opportunities");
  }
  if (!reportForm?.esrs2?.metricsTargets?.trim()) {
    esrs2Missing.push("Metrics and targets");
  }

  const s1Missing = [];
  if (!reportForm?.s1?.workforcePolicies?.trim()) {
    s1Missing.push("Workforce policies");
  }
  if (!reportForm?.s1?.diversityInclusion?.trim()) {
    s1Missing.push("Diversity & inclusion");
  }

  const g1Missing = [];
  if (!reportForm?.g1?.antiCorruption?.trim()) {
    g1Missing.push("Anti-corruption");
  }
  if (!reportForm?.g1?.whistleblowing?.trim()) {
    g1Missing.push("Whistleblowing");
  }

  const materialityMissing = [];
  if (
    !Array.isArray(reportForm?.materialityTopics) ||
    reportForm.materialityTopics.length === 0
  ) {
    materialityMissing.push("Materiality topics");
  } else {
    reportForm.materialityTopics.forEach((topic, index) => {
      if (!topic.topicCode?.trim()) {
        materialityMissing.push(`Topic ${index + 1}: topic code`);
      }
      if (!topic.topicLabel?.trim()) {
        materialityMissing.push(`Topic ${index + 1}: topic label`);
      }
      if (!topic.rationale?.trim()) {
        materialityMissing.push(`Topic ${index + 1}: rationale`);
      }
      if (!topic.stakeholdersConsulted?.trim()) {
        materialityMissing.push(`Topic ${index + 1}: stakeholders consulted`);
      }
    });
  }

  const sections = [
    { key: "ESRS 2", missing: esrs2Missing, total: 4 },
    { key: "E1 Climate", missing: e1Missing, total: 4 },
    { key: "S1 Workforce", missing: s1Missing, total: 2 },
    { key: "G1 Business Conduct", missing: g1Missing, total: 2 },
    {
      key: "Materiality",
      missing: materialityMissing,
      total: Math.max(reportForm?.materialityTopics?.length || 1, 1) * 4
    }
  ];

  return sections.map((section) => {
    const completeness = Math.max(
      0,
      Math.round(
        ((section.total - section.missing.length) / section.total) * 100
      )
    );

    return {
      section: section.key,
      completeness,
      missing: section.missing
    };
  });
};

const getPriorityColor = (priority) => {
  const value = String(priority || "").toLowerCase();

  if (value === "high") {
    return {
      bg: "#fee2e2",
      text: "#991b1b",
      border: "#fecaca"
    };
  }

  if (value === "medium") {
    return {
      bg: "#fef3c7",
      text: "#92400e",
      border: "#fde68a"
    };
  }

  return {
    bg: "#dcfce7",
    text: "#166534",
    border: "#bbf7d0"
  };
};

const parseRecommendationsText = (rawText) => {
  if (!rawText || typeof rawText !== "string") return [];

  const blocks = rawText
    .split(/\n\s*\n(?=\d+\.)/)
    .map((block) => block.trim())
    .filter(Boolean);

  return blocks.map((block, index) => {
    const lines = block.split("\n").map((line) => line.trim());

    const title = (lines[0] || "").replace(/^\d+\.\s*/, "").trim();

    const getField = (label) => {
      const line = lines.find((l) => l.startsWith(`${label}:`));
      return line ? line.replace(`${label}:`, "").trim() : "";
    };

    const category = getField("Category");
    const priority = getField("Priority");
    const timeline = getField("Timeline");
    const esrsReference = getField("ESRS Reference");

    const extractSectionLines = (sectionTitle, nextSectionTitles = []) => {
      const startIndex = lines.findIndex((l) => l === sectionTitle);
      if (startIndex === -1) return [];

      let endIndex = lines.length;

      for (const nextTitle of nextSectionTitles) {
        const foundIndex = lines.findIndex(
          (l, i) => i > startIndex && l === nextTitle
        );
        if (foundIndex !== -1 && foundIndex < endIndex) {
          endIndex = foundIndex;
        }
      }

      return lines
        .slice(startIndex + 1, endIndex)
        .map((l) => l.trim())
        .filter(Boolean);
    };

    const currentGap = extractSectionLines("Current Gap:", [
      "Risk / Impact:",
      "Recommended Actions:",
      "Suggested KPIs:"
    ]).join(" ");

    const riskImpact = extractSectionLines("Risk / Impact:", [
      "Recommended Actions:",
      "Suggested KPIs:"
    ]).join(" ");

    const actions = extractSectionLines("Recommended Actions:", [
      "Suggested KPIs:"
    ]).map((line) => line.replace(/^- /, "").trim());

    const suggestedKPIs = extractSectionLines("Suggested KPIs:").map((line) =>
      line.replace(/^- /, "").trim()
    );

    return {
      id: `${title}-${index}`,
      title,
      category,
      priority,
      timeline,
      esrsReference,
      currentGap,
      riskImpact,
      actions,
      suggestedKPIs
    };
  });
};

function GuidedSteps({ step }) {
  const steps = [
    "Fill Company Information",
    "Complete ESRS 2",
    "Add ESG Data",
    "Define Materiality",
    "Generate AI Draft",
    "Review & Export"
  ];

  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        marginBottom: "20px",
        flexWrap: "wrap"
      }}
    >
      {steps.map((label, index) => (
        <div
          key={index}
          style={{
            padding: "8px 12px",
            borderRadius: "999px",
            background: index <= step ? "#7c3aed" : "#e5e7eb",
            color: index <= step ? "#fff" : "#374151",
            fontSize: "12px",
            fontWeight: "bold"
          }}
        >
          {label}
        </div>
      ))}
    </div>
  );
}

function FieldLabel({ children, helpKey }) {
  return (
    <label
      style={{
        fontWeight: "bold",
        display: "inline-flex",
        alignItems: "center",
        marginBottom: "6px"
      }}
    >
      {children}
      <HelpTooltip content={HELP[helpKey]} />
    </label>
  );
}

function AdminDashboard({ API, authHeaders, setStatusMessage }) {
  const [summary, setSummary] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [companies, setCompanies] = React.useState([]);
  const [supportCases, setSupportCases] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const adminFetch = async (path, options = {}) => {
  const response = await fetch(`${API}${path}`, {
    credentials: "include",
    ...options,
    headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...(options.headers || {})
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Admin request failed.");
    }

    return data;
  };

  const loadAdminData = async () => {
    try {
      setLoading(true);

      const [summaryData, usersData, companiesData, supportCasesData] =
        await Promise.all([
          adminFetch("/admin/summary"),
          adminFetch("/admin/users"),
          adminFetch("/admin/companies"),
          adminFetch("/admin/support-cases")
        ]);

      setSummary(summaryData);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setSupportCases(Array.isArray(supportCasesData) ? supportCasesData : []);
      setStatusMessage("Admin dashboard refreshed.");
    } catch (err) {
      setStatusMessage(`Admin dashboard failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadAdminData();
  }, []);

  const updateUser = async (userId, updates) => {
    await adminFetch(`/admin/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(updates)
    });

    await loadAdminData();
  };

  const updateCompany = async (companyId, updates) => {
    await adminFetch(`/admin/companies/${companyId}`, {
      method: "PATCH",
      body: JSON.stringify(updates)
    });

    await loadAdminData();
  };

  const updateSupportCase = async (caseId, updates) => {
    await adminFetch(`/admin/support-cases/${caseId}`, {
      method: "PATCH",
      body: JSON.stringify(updates)
    });

    await loadAdminData();
  };

  return (
    <div style={{ display: "grid", gap: "24px" }}>
      <Button onClick={loadAdminData} disabled={loading}>
        {loading ? "Refreshing..." : "Refresh Admin Dashboard"}
      </Button>

      {summary && (
        <DashboardGrid min="180px">
          <Card style={{ padding: "16px" }}>
            <strong>Users</strong>
            <div style={{ fontSize: "28px", marginTop: "8px" }}>
              {summary.users}
            </div>
          </Card>

          <Card style={{ padding: "16px" }}>
            <strong>Companies</strong>
            <div style={{ fontSize: "28px", marginTop: "8px" }}>
              {summary.companies}
            </div>
          </Card>

          <Card style={{ padding: "16px" }}>
            <strong>Open Cases</strong>
            <div style={{ fontSize: "28px", marginTop: "8px" }}>
              {summary.openCases}
            </div>
          </Card>

          <Card style={{ padding: "16px" }}>
            <strong>Enterprise Users</strong>
            <div style={{ fontSize: "28px", marginTop: "8px" }}>
              {summary.enterpriseUsers}
            </div>
          </Card>
        </DashboardGrid>
      )}

      <Card style={{ padding: "20px" }}>
        <h3>Users</h3>

        <div style={{ display: "grid", gap: "12px" }}>
          {users.map((item) => (
            <div
              key={item._id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "12px",
                display: "grid",
                gap: "8px"
              }}
            >
              <strong>{item.fullName || item.email}</strong>
              <div>{item.email}</div>
              <div>Company: {item.companyId?.name || item.companyName || "N/A"}</div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <select
                  value={item.role || "preparer"}
                  onChange={(e) =>
                    updateUser(item._id, { role: e.target.value })
                  }
                >
                  <option value="admin">admin</option>
                  <option value="preparer">preparer</option>
                  <option value="reviewer">reviewer</option>
                  <option value="approver">approver</option>
                </select>

                <select
                  value={item.plan || "starter"}
                  onChange={(e) =>
                    updateUser(item._id, { plan: e.target.value })
                  }
                >
                  <option value="free">free</option>
                  <option value="starter">starter</option>
                  <option value="pro">pro</option>
                  <option value="enterprise">enterprise</option>
                </select>

                <select
                  value={item.subscriptionStatus || "inactive"}
                  onChange={(e) =>
                    updateUser(item._id, {
                      subscriptionStatus: e.target.value
                    })
                  }
                >
                  <option value="inactive">inactive</option>
                  <option value="trialing">trialing</option>
                  <option value="active">active</option>
                  <option value="past_due">past_due</option>
                  <option value="canceled">canceled</option>
                  <option value="cancelled">cancelled</option>
                  <option value="unpaid">unpaid</option>
                  <option value="paused">paused</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ padding: "20px" }}>
        <h3>Companies</h3>

        <div style={{ display: "grid", gap: "12px" }}>
          {companies.map((company) => (
            <div
              key={company._id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "12px",
                display: "grid",
                gap: "8px"
              }}
            >
              <strong>{company.name}</strong>
              <div>Owner: {company.ownerId?.email || "N/A"}</div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <select
                  value={company.plan || "starter"}
                  onChange={(e) =>
                    updateCompany(company._id, { plan: e.target.value })
                  }
                >
                  <option value="free">free</option>
                  <option value="starter">starter</option>
                  <option value="pro">pro</option>
                  <option value="enterprise">enterprise</option>
                </select>

                <select
                  value={company.status || "active"}
                  onChange={(e) =>
                    updateCompany(company._id, { status: e.target.value })
                  }
                >
                  <option value="active">active</option>
                  <option value="suspended">suspended</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ padding: "20px" }}>
        <h3>Support Cases</h3>

        <div style={{ display: "grid", gap: "12px" }}>
          {supportCases.length === 0 ? (
            <p style={{ color: "#6b7280" }}>No support cases yet.</p>
          ) : (
            supportCases.map((supportCase) => (
              <div
                key={supportCase._id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "12px",
                  display: "grid",
                  gap: "8px"
                }}
              >
                <strong>{supportCase.subject}</strong>
                <div>{supportCase.message}</div>
                <div>User: {supportCase.userEmail || supportCase.userId?.email || "N/A"}</div>
                <div>Company: {supportCase.companyId?.name || "N/A"}</div>

                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <select
                    value={supportCase.status || "open"}
                    onChange={(e) =>
                      updateSupportCase(supportCase._id, {
                        status: e.target.value
                      })
                    }
                  >
                    <option value="open">open</option>
                    <option value="in_progress">in_progress</option>
                    <option value="resolved">resolved</option>
                    <option value="closed">closed</option>
                  </select>

                  <select
                    value={supportCase.priority || "normal"}
                    onChange={(e) =>
                      updateSupportCase(supportCase._id, {
                        priority: e.target.value
                      })
                    }
                  >
                    <option value="low">low</option>
                    <option value="normal">normal</option>
                    <option value="high">high</option>
                    <option value="urgent">urgent</option>
                  </select>
                </div>

                <textarea
                  value={supportCase.adminNotes || ""}
                  placeholder="Admin notes"
                  rows={2}
                  onChange={(e) =>
                    setSupportCases((prev) =>
                      prev.map((item) =>
                        item._id === supportCase._id
                          ? { ...item, adminNotes: e.target.value }
                          : item
                      )
                    )
                  }
                  onBlur={(e) =>
                    updateSupportCase(supportCase._id, {
                      adminNotes: e.target.value
                    })
                  }
                  style={{
                    padding: "10px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db"
                  }}
                />
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

function MainPlatform() {
  const resetParams = new URLSearchParams(window.location.search);
  const currentPath = window.location.pathname;
  const resetTokenFromUrl = resetParams.get("resetToken");
  const resetEmailFromUrl = resetParams.get("email");

  const isResetPasswordFlow =
    Boolean(resetTokenFromUrl) && Boolean(resetEmailFromUrl);

  const [reviewerEmail, setReviewerEmail] = useState("");
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [user, setUser] = useState(null);

  const [authMode, setAuthMode] = useState(
    isResetPasswordFlow ? "resetPassword" : "login"
  );

  const AUTO_CHECKOUT_LOCK_KEY = "ecovanta_auto_checkout_started";

const [activePage, setActivePage] = useState(() =>
  window.location.pathname === "/app/copilot"
    ? "Copilot"
    : "Dashboard"
);

const clearPendingPlanFromUrl = () => {
  const url = new URL(window.location.href);

  url.searchParams.delete("plan");
  url.searchParams.delete("signup");
  url.searchParams.delete("checkout");

  window.history.replaceState(
    {},
    "",
    url.pathname + url.search
  );
};

const [selectedBillingCycle, setSelectedBillingCycle] = useState(() => {
  const savedCycle = localStorage.getItem("ecovanta_selected_billing_cycle");

  if (savedCycle === "annual" || savedCycle === "monthly") {
    return savedCycle;
  }

  return "monthly";
});

const updateSelectedBillingCycle = (cycle) => {
  const safeCycle = cycle === "annual" ? "annual" : "monthly";

  setSelectedBillingCycle(safeCycle);
  localStorage.setItem("ecovanta_selected_billing_cycle", safeCycle);
};


const clearAutoCheckoutLock = () => {
  sessionStorage.removeItem(AUTO_CHECKOUT_LOCK_KEY);
};

const hasAutoCheckoutStarted = () => {
  return (
    sessionStorage.getItem(AUTO_CHECKOUT_LOCK_KEY) === "true"
  );
};
  const [reviewerEmailByReport, setReviewerEmailByReport] = useState({});

const markAutoCheckoutStarted = () => {
  sessionStorage.setItem(AUTO_CHECKOUT_LOCK_KEY, "true");
};
// authMode can now be:
// login | register | forgotPassword | resetPassword
const [authForm, setAuthForm] = useState({
  
  fullName: "",
  email: "",
  password: "",

  companyName: "",
  companyLocation: "",
  country: "",
  sector: "technology",
  companySize: "1-10",
  jobTitle: "",

  mfaCode: "",

  resetEmail: "",
  resetToken: "",
  newPassword: "",
acceptedLegal: false
});
const [authStep, setAuthStep] = useState("login");
// login | register | mfa
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const [reports, setReports] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalReports: 0,
    averageScore: 0,
    highRisk: 0,
    moderateRisk: 0,
    lowRisk: 0
  });

  const [pendingUpgradePlan, setPendingUpgradePlan] = useState(() => {
  const params = new URLSearchParams(window.location.search);
  const planFromUrl = params.get("plan") || "";

  if (
    !planFromUrl ||
    planFromUrl === "[object Object]" ||
    planFromUrl.includes("[object")
  ) {
    params.delete("plan");
    params.delete("signup");

    window.history.replaceState(
      {},
      "",
      window.location.pathname
    );

    return "";
  }

  return planFromUrl;
});
 const [benchmark, setBenchmark] = useState(60);
const [selectedReportId, setSelectedReportId] = useState("");
const [reportForm, setReportForm] = useState(initialReportForm);
const benchmarkComparisonData = useMemo(() => {
  const sector = reportForm?.sector || "tech";

  const sectorBenchmarks =
    DEFAULT_SECTOR_BENCHMARKS[sector] ||
    DEFAULT_SECTOR_BENCHMARKS.tech;

  const companyScore = Number(
    reportForm?.scorecard?.overallScore ||
      analytics?.averageScore ||
      0
  );

  return [
    {
      name: "Your Company",
      value: companyScore
    },
    {
      name: "Sector Average",
      value: sectorBenchmarks.sectorAverage
    },
    {
      name: "Top Quartile",
      value: sectorBenchmarks.topQuartile
    }
  ];
}, [reportForm, analytics]);
const [recommendationFilter, setRecommendationFilter] = useState("all");

  const isUserLoaded = Boolean(user);
  const normalizeUserPlan = (plan) => {
  const normalized = String(plan || "").toLowerCase();

  if (normalized === "free") return PLAN_KEYS.STARTER;
  if (normalized === PLAN_KEYS.PRO) return PLAN_KEYS.PRO;
  if (normalized === PLAN_KEYS.ENTERPRISE) return PLAN_KEYS.ENTERPRISE;

  return PLAN_KEYS.STARTER;
};

  const currentPlan = normalizeUserPlan(user?.plan);
  const currentPlanConfig = getPlanConfig(currentPlan);

 
  const hasAiDraftAccess =
  currentPlan === PLAN_KEYS.PRO ||
  currentPlan === PLAN_KEYS.ENTERPRISE;
  const hasCopilotAccess =
  currentPlan === PLAN_KEYS.PRO ||
  currentPlan === PLAN_KEYS.ENTERPRISE;
  const hasPdfExportAccess = canAccess(currentPlan, "pdfExport");
  const hasAdvancedAnalyticsAccess = canAccess(currentPlan, "advancedAnalytics");
  const hasWorkflowAccess = canAccess(currentPlan, "workflow");
  const hasAuditTrailAccess = canAccess(currentPlan, "auditTrail");


  const authHeaders = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const fetchJson = useCallback(async (url, options = {}) => {
   const res = await fetch(url, {
  credentials: "include",
  ...options
});
    const contentType = res.headers.get("content-type") || "";

    let data;
    if (contentType.includes("application/json")) {
      data = await res.json();
    } else {
      data = await res.text();
    }

    if (!res.ok) {
      const message =
        typeof data === "string"
          ? data
          : data?.error || data?.details || "Request failed";
      throw new Error(message);
    }

    return data;
  }, []);


  const normalizeAiDraft = useCallback((aiDraft) => {
    return {
      executiveSummary:
        typeof aiDraft?.executiveSummary === "string"
          ? aiDraft.executiveSummary
          : JSON.stringify(aiDraft?.executiveSummary ?? "", null, 2),

      disclosureDraft:
        typeof aiDraft?.disclosureDraft === "string"
          ? aiDraft.disclosureDraft
          : JSON.stringify(aiDraft?.disclosureDraft ?? "", null, 2),

      dataGaps:
        typeof aiDraft?.dataGaps === "string"
          ? aiDraft.dataGaps
          : Array.isArray(aiDraft?.dataGaps)
          ? aiDraft.dataGaps.join("\n- ")
          : JSON.stringify(aiDraft?.dataGaps ?? "", null, 2),

      recommendations:
        typeof aiDraft?.recommendations === "string"
          ? aiDraft.recommendations
          : Array.isArray(aiDraft?.recommendations)
          ? aiDraft.recommendations.join("\n- ")
          : JSON.stringify(aiDraft?.recommendations ?? "", null, 2)
    };
  }, []);

  const handleAuthField = (field, value) => {
    setAuthForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const setTopField = (field, value) => {
    setReportForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const setNestedField = (section, field, value) => {
    setReportForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const isEditable = () => {
    if (reportForm.reviewStatus === "draft") return true;

    if (
      reportForm.reviewStatus === "in_review" &&
      ["reviewer", "approver", "admin"].includes(user?.role)
    ) {
      return true;
    }

    return false;
  };

const login = useCallback(async () => {
  try {
    setLoading(true);
    setStatusMessage("");

    const data = await fetchJson(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: authForm.email,
        password: authForm.password
      })
    });
if (data.requiresMfa) {
  setAuthStep("mfa");
  setLoading(false);
  return;
}
    setToken(data.token);
    localStorage.setItem("token", data.token);
setUser(data.user);
    if (pendingUpgradePlan === "pro" || pendingUpgradePlan === "enterprise") {
      setStatusMessage(
        `Logged in. Redirecting to ${pendingUpgradePlan} checkout...`
      );
    } else {
      setStatusMessage("Logged in successfully.");
    }
  } catch (err) {
    console.error("Login error:", err);
    setStatusMessage(`Login failed: ${err.message}`);
  } finally {
    setLoading(false);
  }
}, [authForm.email, authForm.password, fetchJson, pendingUpgradePlan]);

const requestPasswordReset = async () => {
  try {
    setLoading(true);

    await fetchJson(`${API}/auth/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: authForm.resetEmail || authForm.email
      })
    });

    setStatusMessage("If the email exists, a reset link has been sent.");
  } catch (err) {
    setStatusMessage(err?.message || "Password reset request failed.");
  } finally {
    setLoading(false);
  }
};

const resetPassword = async () => {
  try {
    setLoading(true);

    await fetchJson(`${API}/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: authForm.email,
        token: authForm.resetToken,
        newPassword: authForm.newPassword
      })
    });

    setStatusMessage("Password reset successful. Please log in.");
    setAuthMode("login");
  } catch (err) {
    setStatusMessage(err?.message || "Password reset failed.");
  } finally {
    setLoading(false);
  }
};

const verifyMfa = async () => {
  try {
    setLoading(true);

    const data = await fetchJson(
      `${API}/auth/verify-mfa`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: authForm.email,
          code: authForm.mfaCode
        })
      }
    );

    setToken(data.token);

    localStorage.setItem(
      "token",
      data.token
    );

    setUser(data.user);

    setAuthStep("login");

    setStatusMessage(
      "MFA verification successful."
    );
  } catch (err) {
    console.error(err);

    setStatusMessage(
      err?.message || "MFA failed."
    );
  } finally {
    setLoading(false);
  }
};
const register = useCallback(async () => {
  try {
    setLoading(true);
    setStatusMessage("");

    if (!authForm.acceptedLegal) {
      setStatusMessage(
        "Please accept the Terms of Use and Privacy Policy to create an account."
      );
      setLoading(false);
      return;
    }

const registrationResult = await fetchJson(`${API}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        fullName: authForm.fullName,
        email: authForm.email,
        password: authForm.password,
        companyName: authForm.companyName,
        companyLocation: authForm.companyLocation,
        country: authForm.country,
        sector: authForm.sector,
        companySize: authForm.companySize,
        jobTitle: authForm.jobTitle,
        acceptedLegal: authForm.acceptedLegal
      })
    });

    trackEvent("account_created", {
    email: authForm.email,
    company: authForm.companyName || "",
    sector: authForm.sector || "",
    companySize: authForm.companySize || "",
    pendingUpgradePlan: pendingUpgradePlan || "starter",
    path: window.location.pathname
  });

    if (
      pendingUpgradePlan === "pro" ||
      pendingUpgradePlan === "enterprise"
    ) {
      setStatusMessage(
        `Registration successful. Please log in to continue to ${pendingUpgradePlan} checkout.`
      );
    } else {
      setStatusMessage("Registration successful. Please log in.");
    }



    setAuthMode("login");

    setAuthForm((prev) => ({
      ...prev,
      password: "",
      acceptedLegal: false
    }));
  } catch (err) {
    console.error("Register error:", err);

    setStatusMessage(
      `Register failed: ${err?.message || "Unknown error"}`
    );
  } finally {
    setLoading(false);
  }
}, [
  authForm.fullName,
  authForm.email,
  authForm.password,
  authForm.companyName,
  authForm.companyLocation,
  authForm.country,
  authForm.sector,
  authForm.companySize,
  authForm.jobTitle,
  authForm.acceptedLegal,
  fetchJson,
  pendingUpgradePlan
]);
  const logout = () => {
    localStorage.removeItem("token");
    setToken("");
    setUser(null);
    setReports([]);
    setAuditLogs([]);
    setSelectedReportId("");
    setReportForm(initialReportForm);
    setStatusMessage("Logged out.");
  };

  const loadUser = useCallback(async () => {
    if (!token) return;

    try {
      const data = await fetchJson(`${API}/auth/me`, {
        headers: authHeaders
      });
      setUser(data);
    } catch (err) {
      console.error("Load user error:", err);
    }
  }, [token, authHeaders, fetchJson]);

  const loadReports = useCallback(async () => {
    if (!token) return;

    try {
      const data = await fetchJson(`${API}/reports`, {
        headers: authHeaders
      });
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load reports error:", err);
      setStatusMessage(`Could not load reports: ${err.message}`);
    }
  }, [token, authHeaders, fetchJson]);

  const loadAnalytics = useCallback(async () => {
    if (!token) return;

    try {
      const data = await fetchJson(`${API}/reports`, {
        headers: authHeaders
      });

      const safeReports = Array.isArray(data) ? data : [];
      const scores = safeReports.map((report) =>
        Number(report.scorecard?.overallScore || 0)
      );

      const totalReports = safeReports.length;
      const averageScore = totalReports
        ? Math.round(
            scores.reduce((sum, value) => sum + value, 0) / totalReports
          )
        : 0;

      const highRisk = scores.filter((score) => score < 60).length;
      const moderateRisk = scores.filter(
        (score) => score >= 60 && score < 80
      ).length;
      const lowRisk = scores.filter((score) => score >= 80).length;

      setAnalytics({
        totalReports,
        averageScore,
        highRisk,
        moderateRisk,
        lowRisk
      });
    } catch (err) {
      console.error("Load analytics error:", err);
    }
  }, [token, authHeaders, fetchJson]);

 const loadAuditLogs = useCallback(async () => {
  if (!token || !canAccess(currentPlan, "auditTrail")) {
    setAuditLogs([]);
    return;
  }

    try {
      const data = await fetchJson(`${API}/audit`, {
        headers: authHeaders
      });
      setAuditLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load audit logs error:", err);
    }
}, [token, authHeaders, fetchJson, currentPlan]);

  const loadBenchmark = useCallback(async () => {
    try {
      const data = await fetchJson(`${API}/benchmark/${reportForm.sector}`);
      setBenchmark(Number(data.benchmark || 60));
    } catch (err) {
      console.error("Load benchmark error:", err);
      setBenchmark(
        DEFAULT_SECTOR_BENCHMARKS[reportForm.sector]?.sectorAverage || 60
      );
    }
  }, [reportForm.sector, fetchJson]);

  const refreshDashboard = useCallback(async () => {
    await Promise.all([
      loadUser(),
      loadReports(),
      loadAnalytics(),
      loadAuditLogs()
    ]);
  }, [loadUser, loadReports, loadAnalytics, loadAuditLogs]);

useEffect(() => {
  if (!isResetPasswordFlow) {
    return;
  }

  setAuthMode("resetPassword");

  setAuthForm((prev) => ({
    ...prev,
    email: resetEmailFromUrl,
    resetEmail: resetEmailFromUrl,
    resetToken: resetTokenFromUrl,
    newPassword: ""
  }));
}, [isResetPasswordFlow, resetEmailFromUrl, resetTokenFromUrl]);

  useEffect(() => {
    if (!token) return;
    refreshDashboard();
  }, [token, refreshDashboard]);

  useEffect(() => {
    loadBenchmark();
  }, [loadBenchmark]);


useEffect(() => {
  const params = new URLSearchParams(window.location.search);

  const resetToken = params.get("resetToken");
  const resetEmail = params.get("email");

  if (resetToken && resetEmail) {
    setAuthMode("resetPassword");

    setAuthForm((prev) => ({
      ...prev,
      email: resetEmail,
      resetEmail,
      resetToken,
      newPassword: ""
    }));
  }
}, []);

useEffect(() => {
  initAnalytics();
}, []);

useEffect(() => {
  if (user?.email) {
    identifyUser(user);
  }
}, [user]);

  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const checkoutStatus = params.get("checkout");

  // Clear the lock after Stripe redirects back
  if (checkoutStatus === "success" || checkoutStatus === "cancelled") {
    clearAutoCheckoutLock();
    setPendingUpgradePlan("");
    clearPendingPlanFromUrl();

    if (checkoutStatus === "success") {
      setStatusMessage("Subscription activated successfully.");
      refreshDashboard();
      setActivePage("Subscription");
    }

    if (checkoutStatus === "cancelled") {
      setStatusMessage("Checkout cancelled.");
    }

    return;
  }

  if (!token || !pendingUpgradePlan) return;

  if (!(pendingUpgradePlan === "pro" || pendingUpgradePlan === "enterprise")) {
    return;
  }

  // Prevent looping checkout launches
  if (hasAutoCheckoutStarted()) return;

  markAutoCheckoutStarted();

  const runCheckout = async () => {
    try {
      await upgradePlan(pendingUpgradePlan);
    } catch (err) {
      console.error("Auto-upgrade error:", err);
      clearAutoCheckoutLock();
    }
  };

  runCheckout();
}, [token, pendingUpgradePlan, refreshDashboard]);

  

const requireFeature = (featureName, message) => {
  if (!canAccess(currentPlan, featureName)) {
    alert(message);
    return false;
  }
  return true;
};

const changeSubscriptionPlan = async (targetPlanKey, billingCycle) => {
  if (!token) {
    alert("Login required.");
    return;
  }

  const confirmed = window.confirm(
  `Change your subscription to ${String(targetPlanKey).toUpperCase()} ${billingCycle || selectedBillingCycle}?`
  );

  if (!confirmed) return;

  try {
    const data = await fetchJson(`${API}/billing/change-subscription-plan`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders
      },
      body: JSON.stringify({
        targetPlanKey,
        billingCycle: billingCycle || selectedBillingCycle
      })
    });

    setUser((prev) => ({
      ...prev,
      plan: data.plan,
      subscriptionStatus: data.subscriptionStatus
    }));

    setStatusMessage(data.message || "Subscription updated.");
    await refreshDashboard();
  } catch (err) {
    console.error("Change subscription error:", err);
    alert(`Subscription change failed: ${err.message}`);
  }
};

const upgradePlan = async (plan, billingCycle) => {
  const safePlan =
    typeof plan === "string"
      ? plan
      : plan?.key || plan?.id || plan?.name || "";

    const savedBillingCycle = localStorage.getItem(
  "ecovanta_selected_billing_cycle"
);

const safeBillingCycle =
  billingCycle === "annual" || billingCycle === "monthly"
    ? billingCycle
    : savedBillingCycle === "annual" || savedBillingCycle === "monthly"
    ? savedBillingCycle
    : "monthly";

updateSelectedBillingCycle(safeBillingCycle);

  if (!safePlan) {
    setStatusMessage("Invalid plan selected.");
    return;
  }

  if (!token) {
    alert("Login required.");
    return;
  }

  try {
    setPendingUpgradePlan(safePlan);
const data = await fetchJson(
  `${API}/billing/create-checkout-session`,
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({
      plan: safePlan,
      billingCycle: safeBillingCycle,
    }),
  }
);

if (data?.url) {
  trackEvent("subscription_started", {
    plan: safePlan,
    billingCycle: safeBillingCycle,
    source: "pricing_page"
  });

  window.location.assign(data.url);
} else {
  throw new Error("Missing checkout URL.");
}
  } catch (err) {
    console.error("Upgrade error:", err);
    setStatusMessage(err?.message || "Upgrade failed.");
    alert(`Upgrade failed: ${err?.message || "Unknown error"}`);
  }
};

const openBillingPortal = async () => {
  if (!token) {
    alert("Login required.");
    return;
  }

  try {
    const data = await fetchJson(`${API}/billing/create-portal-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders
      }
    });

    if (!data.url) {
      throw new Error("Billing portal URL was not returned");
    }

    window.location.href = data.url;
  } catch (err) {
    console.error("Billing portal error:", err);
    alert(`Failed to open billing portal: ${err.message}`);
  }
};


  const cancelSubscription = async () => {
    if (!token) {
      alert("Login required.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to cancel your subscription?"
    );

    if (!confirmed) return;

    try {
      const data = await fetchJson(`${API}/billing/cancel-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        }
      });

      setStatusMessage(data.message || "Subscription cancellation requested.");

      addNotification({
        type: "billing",
        priority: "high",
        title: "Subscription cancellation requested",
        message: data.message || "Your subscription cancellation request was submitted successfully.",
        targetPage: "Subscription"
      });

      await refreshDashboard();
    } catch (err) {
      console.error("Cancel subscription error:", err);
      alert(`Cancel failed: ${err.message}`);
    }
  };

  const addMaterialityTopic = () => {
    setReportForm((prev) => ({
      ...prev,
      materialityTopics: [
        ...prev.materialityTopics,
        { ...defaultMaterialityTopic }
      ]
    }));
  };

  const removeMaterialityTopic = (index) => {
    setReportForm((prev) => ({
      ...prev,
      materialityTopics: prev.materialityTopics.filter((_, i) => i !== index)
    }));
  };

  const updateMaterialityTopic = (index, path, value) => {
    setReportForm((prev) => {
      const nextTopics = [...prev.materialityTopics];
      const current = { ...nextTopics[index] };

      if (path.includes(".")) {
        const [section, field] = path.split(".");
        current[section] = {
          ...current[section],
          [field]: value
        };
      } else {
        current[path] = value;
      }

      const scores = calculateMaterialityScores(current);

      nextTopics[index] = {
        ...current,
        impactScore100: scores.impactScore100,
        financialScore100: scores.financialScore100,
        overallMaterialityScore: scores.overallMaterialityScore,
        isMaterial: scores.isMaterial
      };

      return {
        ...prev,
        materialityTopics: nextTopics
      };
    });
  };

  const generateAiDraft = async () => {
  if (!token) {
    alert("Login required.");
    return;
  }

  if (
    !requireFeature(
      "aiDraft",
      "AI Draft is available on Pro and Enterprise plans only."
    )
  ) {
    return;
  }

  const payload = {
    companyName: String(reportForm.companyName || "").trim(),
    sector: String(reportForm.sector || "").trim(),
    reportingYear: reportForm.reportingYear,
    esrs2: reportForm.esrs2,
    e1: reportForm.e1,
    s1: reportForm.s1,
    g1: reportForm.g1,
    materialityTopics: reportForm.materialityTopics.map((topic) => {
      const scores = calculateMaterialityScores(topic);

      return {
        ...topic,
        stakeholdersConsulted: topic.stakeholdersConsulted
          ? topic.stakeholdersConsulted
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : [],
        impactScore100: scores.impactScore100,
        financialScore100: scores.financialScore100,
        overallMaterialityScore: scores.overallMaterialityScore,
        isMaterial: scores.isMaterial
      };
    })
  };

  if (!payload.companyName || !payload.sector) {
    setStatusMessage(
      "Company name and sector must be filled before AI draft."
    );
    return;
  }

  try {
    setAiLoading(true);
    setStatusMessage("");

    const data = await fetchJson(`${API}/ai/ai-draft`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders
      },
      body: JSON.stringify(payload)
    });

    setReportForm((prev) => ({
      ...prev,
      aiDraft: {
        ...prev.aiDraft,
        executiveSummary: data.executiveSummary || "",
        disclosureDraft: data.disclosureDraft || "",
        dataGaps: data.dataGaps || "",
        recommendations: data.recommendations || ""
      }
    }));

    await refreshDashboard();

    setStatusMessage("AI draft generated.");

    addNotification({
      type: "report",
      priority: "normal",
      title: "AI draft generated",
      message: `${payload.companyName} (${payload.reportingYear}) now has an AI-generated executive summary, disclosure draft, data gaps, and recommendations.`,
      targetPage: "AI Draft"
    });
  } catch (err) {
    console.error("AI draft error:", err);

    const errorCode =
      err?.code ||
      err?.response?.data?.code;

    if (errorCode === "CREDIT_LIMIT_REACHED") {
      setStatusMessage(
        "You have used all monthly AI credits. Upgrade your plan or wait until the next billing cycle."
      );
      return;
    }

    if (errorCode === "PLAN_UPGRADE_REQUIRED") {
      setStatusMessage(
        "AI Draft is available on Pro and Enterprise plans only."
      );
      return;
    }

    setStatusMessage(
      err?.response?.data?.error ||
        err?.message ||
        "AI draft generation failed."
    );
  } finally {
    setAiLoading(false);
  }
};

const addAuditLog = (
  reportId,
  action,
  details = ""
) => {
  setAuditLogs((previousLogs) => [
    {
      id: `${Date.now()}-${Math.random()}`,
      reportId,
      action,
      details,
      actor:
        user?.email ||
        user?.name ||
        "Current user",
      timestamp: new Date().toISOString(),
    },
    ...previousLogs,
  ]);
};

const updateReportStatus = async (reportId, nextStatus) => {
  if (!reportId) {
    alert("Missing report ID.");
    return;
  }

  try {
    setLoading(true);
    setStatusMessage("");

    const updatedReport = await fetchJson(
      `${API}/reports/${reportId}/status`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify({
          status: nextStatus
        })
      }
    );

    const normalizedReport = updatedReport.report || updatedReport;
    const normalizedId = normalizedReport._id || normalizedReport.id || reportId;

    setReports((previousReports) =>
      previousReports.map((report) =>
        (report._id || report.id) === normalizedId
          ? {
              ...report,
              ...normalizedReport,
              _id: normalizedId,
              id: normalizedId
            }
          : report
      )
    );

    if ((reportForm._id || reportForm.id) === normalizedId) {
      setReportForm((previous) => ({
        ...previous,
        ...normalizedReport,
        _id: normalizedId,
        id: normalizedId
      }));
    }

    addAuditLog(
      normalizedId,
      nextStatus === "in_review"
        ? "Sent for Review"
        : nextStatus === "approved"
        ? "Approved"
        : nextStatus === "published"
        ? "Published"
        : "Status Updated",
      `Report status changed to ${nextStatus}.`
    );

    setStatusMessage(`Report status updated to ${nextStatus}.`);

    addNotification({
      type: "report",
      priority: nextStatus === "published" ? "high" : "normal",
      title:
        nextStatus === "published"
          ? "Report published"
          : nextStatus === "approved"
          ? "Report approved"
          : nextStatus === "in_review"
          ? "Report sent for review"
          : "Report status updated",
      message: `${normalizedReport.companyName || reportForm.companyName || "Report"} status changed to ${String(nextStatus).replace(/_/g, " ")}.`,
      targetPage: "Reports"
    });

    await refreshDashboard();
  } catch (err) {
    console.error("Update report status error:", err);
    setStatusMessage(`Status update failed: ${err.message}`);
    alert(`Status update failed: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

const loadReportIntoForm = (report) => {
  if (!report) return;

  const reportId = report._id || report.id || "";

  setSelectedReportId(reportId);

  setReportForm((prev) => ({
    ...prev,
    ...report,
    _id: reportId,
    id: reportId,
    companyName: report.companyName || "",
    sector: report.sector || "technology",
    reportingYear: report.reportingYear || new Date().getFullYear(),

    esrs2: {
      ...prev.esrs2,
      ...(report.esrs2 || {})
    },

    e1: {
      ...prev.e1,
      ...(report.e1 || {})
    },

    s1: {
      ...prev.s1,
      ...(report.s1 || {})
    },

    g1: {
      ...prev.g1,
      ...(report.g1 || {})
    },

    aiDraft: {
      ...prev.aiDraft,
      ...(report.aiDraft || {})
    },

    scorecard: {
      ...prev.scorecard,
      ...(report.scorecard || {})
    },

    materialityTopics: Array.isArray(report.materialityTopics)
      ? report.materialityTopics
      : []
  }));

  setStatusMessage(`Loaded report: ${report.companyName || "Untitled report"}`);
};

const calculatePillarScores = (report) => {
  // Environmental (E)
  let eScore = 0;
  let eMax = 4;

  if (report.e1?.scope1Emissions) eScore++;
  if (report.e1?.scope2Emissions) eScore++;
  if (report.e1?.scope3Emissions) eScore++;
  if (report.e1?.climatePolicies) eScore++;

  // Social (S)
  let sScore = 0;
  let sMax = 2;

  if (report.s1?.workforcePolicies) sScore++;
  if (report.s1?.diversityInclusion) sScore++;

  // Governance (G)
  let gScore = 0;
  let gMax = 4;

  if (report.g1?.antiCorruption) gScore++;
  if (report.g1?.whistleblowing) gScore++;
  if (report.esrs2?.governance) gScore++;
  if (report.esrs2?.strategy) gScore++;

  return {
    E: Math.round((eScore / eMax) * 100),
    S: Math.round((sScore / sMax) * 100),
    G: Math.round((gScore / gMax) * 100)
  };
};

  const saveReport = async () => {
  try {
    setLoading(true);
    setStatusMessage("");

    const isExistingReport = Boolean(
      reportForm._id || reportForm.id
    );

    const reportId =
      reportForm._id || reportForm.id;

    const {
      overallScore,
      riskLevel,
      pillarScores
    } = calculateBig4ESGScore(reportForm);

    const payload = {
      ...reportForm,
      scorecard: {
        ...reportForm.scorecard,
        overallScore,
        riskLevel,
        pillarScores
      }
    };

    const savedReportResponse = await fetchJson(
      isExistingReport
        ? `${API}/reports/${reportId}`
        : `${API}/reports`,
      {
        method: isExistingReport ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify(payload)
      }
    );

    const savedReport =
      savedReportResponse?.report || savedReportResponse;

    const usage =
      savedReportResponse?.usage || null;

    const savedId =
      savedReport._id || savedReport.id;

    const normalizedSavedReport = {
      ...savedReport,
      _id: savedId,
      id: savedId,
      scorecard: {
        ...(savedReport.scorecard || {})
      }
    };

    setReportForm(normalizedSavedReport);

    setReports((prev) => {
      const exists = prev.some(
        (item) =>
          (item._id || item.id) === savedId
      );

      if (exists) {
        return prev.map((item) =>
          (item._id || item.id) === savedId
            ? normalizedSavedReport
            : item
        );
      }

      return [normalizedSavedReport, ...prev];
    });

    setSelectedReportId(savedId);

    await loadReports();
    await refreshDashboard();

    const usageText = usage
      ? ` Credits remaining: ${usage.creditsRemaining}/${usage.monthlyCredits}. Reports used: ${usage.reportsGeneratedThisMonth}/${usage.reportsGeneratedThisMonth + usage.reportsRemainingThisMonth}.`
      : "";

    setStatusMessage(
      `Report saved successfully. Score updated to ${
        normalizedSavedReport.scorecard
          ?.overallScore ?? "N/A"
      }/100.${usageText}`
    );

    addNotification({
      type: "report",
      priority: "normal",
      title: isExistingReport ? "Report updated" : "Report generated",
      message: `${normalizedSavedReport.companyName || "ESG report"} (${normalizedSavedReport.reportingYear || new Date().getFullYear()}) was ${isExistingReport ? "updated" : "generated"} successfully with an ESG score of ${normalizedSavedReport.scorecard?.overallScore ?? "N/A"}/100.`,
      targetPage: "Reports"
    });

    if (usage && Number(usage.monthlyCredits) > 0) {
      const monthlyCredits = Number(usage.monthlyCredits);
      const creditsRemaining = Math.max(0, Number(usage.creditsRemaining || 0));
      const creditsUsed = Math.max(0, monthlyCredits - creditsRemaining);
      const usagePercent = Math.round((creditsUsed / monthlyCredits) * 100);
      const billingPeriod = new Date().toISOString().slice(0, 7);

      if (creditsRemaining === 0 || usagePercent >= 100) {
        addNotificationOnce(
          {
            type: "billing",
            priority: "high",
            title: "AI credits exhausted",
            message: `You have used all ${monthlyCredits} monthly AI credits. Upgrade your plan or wait for the next billing cycle.`,
            targetPage: "Subscription"
          },
          `credits-${billingPeriod}-100`
        );
      } else if (usagePercent >= 90) {
        addNotificationOnce(
          {
            type: "billing",
            priority: "high",
            title: "90% of AI credits used",
            message: `${creditsRemaining} of ${monthlyCredits} monthly AI credits remain.`,
            targetPage: "Subscription"
          },
          `credits-${billingPeriod}-90`
        );
      } else if (usagePercent >= 80) {
        addNotificationOnce(
          {
            type: "billing",
            priority: "normal",
            title: "80% of AI credits used",
            message: `${creditsRemaining} of ${monthlyCredits} monthly AI credits remain.`,
            targetPage: "Subscription"
          },
          `credits-${billingPeriod}-80`
        );
      }
    }
  } catch (err) {
    console.error("Save report error:", err);

    if (err?.code === "REPORT_LIMIT_REACHED") {
      setStatusMessage(
        "You have reached your monthly report limit. Upgrade your plan to continue."
      );
      return;
    }

    if (err?.code === "CREDIT_LIMIT_REACHED") {
      setStatusMessage(
        "You have used all monthly credits. Upgrade your plan to continue."
      );
      return;
    }

    setStatusMessage(
      err?.message
        ? `Save failed: ${err.message}`
        : "Save failed. Please try again."
    );
  } finally {
    setLoading(false);
  }
};

const downloadSingleReportPDF = async (report) => {
  try {
    const reportId = report?._id || report?.id;

    if (!reportId) {
      alert("Please save the report before downloading PDF.");
      return;
    }

   const response = await fetch(`${API}/reports/${reportId}/pdf`, {
  credentials: "include",
  headers: authHeaders
});

    if (!response.ok) {
      throw new Error("Failed to download PDF.");
    }

    const contentType = response.headers.get("Content-Type");

    if (!contentType || !contentType.includes("application/pdf")) {
      throw new Error("Backend did not return a valid PDF.");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${(report.companyName || "report").replace(
      /[^a-z0-9]/gi,
      "_"
    )}.pdf`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("PDF download error:", err);
    alert(`Error downloading PDF: ${err.message}`);
  }
};

const deleteReport = async (report) => {
  const reportId = report?._id || report?.id;

  if (!reportId) {
    alert("Missing report ID.");
    return;
  }

  const confirmed = window.confirm(
    "Are you sure you want to delete this report?"
  );

  if (!confirmed) return;

  try {
    setLoading(true);

    await fetchJson(`${API}/reports/${reportId}`, {
      method: "DELETE",
      headers: {
        ...authHeaders
      }
    });

    setReports((prev) =>
      prev.filter((item) => (item._id || item.id) !== reportId)
    );

    if ((reportForm._id || reportForm.id) === reportId) {
      setSelectedReportId("");
      setReportForm(initialReportForm);
    }

    await refreshDashboard();

    setStatusMessage("Report deleted successfully.");
  } catch (err) {
    console.error("Delete report error:", err);
    setStatusMessage(`Delete failed: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

  const materialityHeatmapData = getMaterialityHeatmapData(
    reportForm.materialityTopics
  );
  const complianceGapData = getComplianceGapData(reportForm);

  const parsedRecommendations = parseRecommendationsText(
    reportForm.aiDraft?.recommendations || ""
  );

  const filteredRecommendations = parsedRecommendations.filter((item) => {
    if (recommendationFilter === "all") return true;
    if (recommendationFilter === "high") {
      return String(item.priority || "").toLowerCase() === "high";
    }
    if (recommendationFilter === "compliance") {
      return String(item.category || "").toLowerCase() === "compliance";
    }
    if (recommendationFilter === "strategy") {
      return String(item.category || "").toLowerCase() === "strategy";
    }
    return true;
  });

  const currentStep = useMemo(() => {
    if (!reportForm.companyName) return 0;
    if (!reportForm.esrs2.governance) return 1;
    if (
      !reportForm.e1.scope1Emissions &&
      !reportForm.e1.scope2Emissions &&
      !reportForm.e1.scope3Emissions
    ) {
      return 2;
    }
    if (!reportForm.materialityTopics.length) return 3;
    if (!reportForm.aiDraft.executiveSummary) return 4;
    return 5;
  }, [reportForm]);

const chartData = reports.map((report) => ({
  company: report.companyName,
  score: Number(report.scorecard?.overallScore || 0)
}));

const getRiskColor = (score) => {
  if (score >= 80) return "#2e7d32";
  if (score >= 60) return "#f57c00";
  return "#d32f2f";
};

const getRiskLabel = (score) => {
  if (score >= 80) return "Low Risk";
  if (score >= 60) return "Medium Risk";
  return "High Risk";
};

const localScore = calculateBig4ESGScore(reportForm);
const scorecard = reportForm.scorecard || {};

const displayScorecard = {
  overallScore: scorecard.overallScore ?? localScore.overallScore,
  riskLevel: scorecard.riskLevel ?? localScore.riskLevel,
  pillarScores: scorecard.pillarScores ?? localScore.pillarScores,
  categoryScores: scorecard.categoryScores || {},
  priorityGaps: scorecard.priorityGaps || [],
  recommendedActions: scorecard.recommendedActions || []
};

const assignReviewer = async (report) => {
  const reportId = report?._id || report?.id;
  const reviewerEmail = reviewerEmailByReport[reportId];

  if (!reportId) {
    alert("Missing report ID.");
    return;
  }

  if (!reviewerEmail) {
    alert("Please enter a reviewer email.");
    return;
  }

  try {
    setLoading(true);
    setStatusMessage("");

    const data = await fetchJson(
      `${API}/reports/${reportId}/assign-reviewer`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify({ reviewerEmail })
      }
    );

    const updatedReport = data.report;

    // Update UI immediately
    setReports((prev) =>
      prev.map((item) =>
        (item._id || item.id) === reportId ? updatedReport : item
      )
    );

    setStatusMessage("Reviewer assigned successfully.");

    await refreshDashboard();
  } catch (err) {
    console.error("Assign reviewer error:", err);
    setStatusMessage(`Assign reviewer failed: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

//Main return
if (!token) {
  return (
<PageContainer
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background:
      "linear-gradient(135deg, #ecfdf5 0%, #f8fafc 45%, #e0f2fe 100%)"
  }}
>
  <div
    style={{
      width: "100%",
      maxWidth: "520px",
      margin: "40px auto",
      background: "#ffffff",
      borderRadius: "24px",
      padding: "32px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
      border: "1px solid #e5e7eb"
    }}
  >
    <div style={{ marginBottom: "24px", textAlign: "center" }}>
      <h1
        style={{
          fontSize: "34px",
          fontWeight: "900",
          marginBottom: "8px",
          color: "#047857"
        }}
      >
        Ecovanta
      </h1>

      <h2
        style={{
          fontSize: "28px",
          fontWeight: "800",
          margin: 0,
          color: "#111827"
        }}
      >
        {authStep === "mfa"
          ? "Verify your identity"
          : authMode === "login"
          ? "Welcome back"
          : "Create your account"}
      </h2>

      <p
        style={{
          color: "#6b7280",
          fontSize: "15px",
          lineHeight: "1.6",
          marginTop: "10px"
        }}
      >
        {authStep === "mfa"
          ? "Enter the verification code sent to your email."
          : authMode === "login"
          ? "Log in to continue your ESG reporting workflow."
          : "Start building CSRD-ready sustainability reports."}
      </p>
    </div>

    {authMode === "register" && authStep !== "mfa" && (
      <>
        <Input
          value={authForm.fullName}
          onChange={(e) =>
            handleAuthField("fullName", e.target.value)
          }
          placeholder="Full name"
          style={{ marginBottom: "12px" }}
        />

        <Input
          value={authForm.companyName}
          onChange={(e) =>
            handleAuthField("companyName", e.target.value)
          }
          placeholder="Company name"
          style={{ marginBottom: "12px" }}
        />

        <Input
          value={authForm.companyLocation}
          onChange={(e) =>
            handleAuthField("companyLocation", e.target.value)
          }
          placeholder="Company location"
          style={{ marginBottom: "12px" }}
        />

        <Input
          value={authForm.country}
          onChange={(e) =>
            handleAuthField("country", e.target.value)
          }
          placeholder="Country"
          style={{ marginBottom: "12px" }}
        />

        <Input
          value={authForm.jobTitle}
          onChange={(e) =>
            handleAuthField("jobTitle", e.target.value)
          }
          placeholder="Job title"
          style={{ marginBottom: "12px" }}
        />

        <select
          value={authForm.sector}
          onChange={(e) =>
            handleAuthField("sector", e.target.value)
          }
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: "10px",
            border: "1px solid #d1d5db",
            marginBottom: "12px"
          }}
        >
          <option value="technology">Technology</option>
          <option value="finance">Finance</option>
          <option value="manufacturing">Manufacturing</option>
          <option value="energy">Energy</option>
          <option value="healthcare">Healthcare</option>
          <option value="retail">Retail</option>
        </select>

        <select
          value={authForm.companySize}
          onChange={(e) =>
            handleAuthField("companySize", e.target.value)
          }
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: "10px",
            border: "1px solid #d1d5db",
            marginBottom: "12px"
          }}
        >
          <option value="1-10">1-10 employees</option>
          <option value="11-50">11-50 employees</option>
          <option value="51-200">51-200 employees</option>
          <option value="201-1000">201-1000 employees</option>
          <option value="1000+">1000+ employees</option>
        </select>
        <label
  style={{
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "14px",
    color: "#374151",
    marginBottom: "16px"
  }}
>
  <input
    type="checkbox"
    checked={authForm.acceptedLegal}
    onChange={(e) =>
      handleAuthField("acceptedLegal", e.target.checked)
    }
    style={{
      marginTop: "4px"
    }}
  />

  <span>
    I agree to the{" "}
    <a href="/terms" target="_blank" rel="noreferrer">
      Terms of Use
    </a>{" "}
    and{" "}
    <a href="/privacy" target="_blank" rel="noreferrer">
      Privacy Policy
    </a>
    .
  </span>
</label>
      </>
    )}

   
{isResetPasswordFlow || authMode === "resetPassword" ? (
  <>
    <Input
      value={authForm.email}
      onChange={(e) =>
        handleAuthField("email", e.target.value)
      }
      placeholder="Business email"
      type="email"
      style={{ marginBottom: "12px" }}
    />

    <Input
      value={authForm.newPassword}
      onChange={(e) =>
        handleAuthField("newPassword", e.target.value)
      }
      placeholder="New password"
      type="password"
      style={{ marginBottom: "16px" }}
    />

    <Button
      onClick={resetPassword}
      disabled={loading}
      style={{
        width: "100%",
        padding: "14px",
        background: "#10b981",
        color: "#fff",
        fontWeight: "700",
        borderRadius: "12px"
      }}
    >
      Reset password
    </Button>

    <Button
      variant="secondary"
      onClick={() => {
        setAuthMode("login");

        window.history.replaceState(
          {},
          "",
          window.location.pathname
        );
      }}
      style={{
        width: "100%",
        padding: "14px",
        marginTop: "12px",
        borderRadius: "12px"
      }}
    >
      Back to login
    </Button>
  </>
) : authMode === "forgotPassword" ? (
  <>
    <Input
      value={authForm.resetEmail}
      onChange={(e) =>
        handleAuthField(
          "resetEmail",
          e.target.value
        )
      }
      placeholder="Business email"
      type="email"
      style={{ marginBottom: "16px" }}
    />

    <Button
      onClick={requestPasswordReset}
      disabled={loading}
      style={{
        width: "100%",
        padding: "14px",
        background: "#10b981",
        color: "#fff",
        fontWeight: "700",
        borderRadius: "12px"
      }}
    >
      Send reset link
    </Button>

      <button
  onClick={() => {
    window.location.href = "/esg-maturity-benchmark";
  }}
>
  Start ESG Maturity Benchmark
</button>

    <Button
      variant="secondary"
      onClick={() =>
        setAuthMode("login")
      }
      style={{
        width: "100%",
        padding: "14px",
        marginTop: "12px",
        borderRadius: "12px"
      }}
    >
      Back to login
    </Button>
  </>
) : (
  <>
    {authStep !== "mfa" && (
      <>
        <Input
          value={authForm.email}
          onChange={(e) =>
            handleAuthField(
              "email",
              e.target.value
            )
          }
          placeholder="Business email"
          type="email"
          style={{ marginBottom: "12px" }}
        />

        <Input
          type="password"
          value={authForm.password}
          onChange={(e) =>
            handleAuthField(
              "password",
              e.target.value
            )
          }
          placeholder="Password"
          style={{ marginBottom: "16px" }}
        />
      </>
    )}

    {authStep === "mfa" && (
      <Input
        value={authForm.mfaCode}
        onChange={(e) =>
          handleAuthField(
            "mfaCode",
            e.target.value
          )
        }
        placeholder="Enter MFA code"
        style={{ marginBottom: "16px" }}
      />
    )}

    <Button
      onClick={() => {
        if (authStep === "mfa") {
          verifyMfa();
          return;
        }

        if (authMode === "login") {
          login();
        } else {
          register();
        }
      }}
      disabled={loading}
      style={{
        width: "100%",
        padding: "14px",
        background: "#10b981",
        color: "#fff",
        fontWeight: "700",
        borderRadius: "12px"
      }}
    >
      {loading
        ? "Please wait..."
        : authStep === "mfa"
        ? "Verify MFA"
        : authMode === "login"
        ? "Login"
        : "Create account"}
    </Button>

    {authStep !== "mfa" && (
      <>
        <Button
          variant="secondary"
          onClick={() =>
            setAuthMode(
              authMode === "login"
                ? "register"
                : "login"
            )
          }
          style={{
            width: "100%",
            padding: "14px",
            marginTop: "12px",
            borderRadius: "12px"
          }}
        >
          {authMode === "login"
            ? "Create an account"
            : "Back to login"}
        </Button>
        {true && (
          <div
            style={{
              marginTop: "14px",
              textAlign: "center"
            }}
          >
            <button
              onClick={() =>
                setAuthMode(
                  "forgotPassword"
                )
              }
              style={{
                background: "none",
                border: "none",
                color: "#10b981",
                cursor: "pointer",
                fontWeight: "600"
              }}
            >
              Forgot password?
            </button>
          </div>
        )}
      </>
    )}

    {authStep === "mfa" && (
      <Button
        variant="secondary"
        onClick={() =>
          setAuthStep("login")
        }
        style={{
          width: "100%",
          padding: "14px",
          marginTop: "12px",
          borderRadius: "12px"
        }}
      >
        Back to login
      </Button>
    )}
  </>
)}

<div
  style={{
    marginTop: "20px",
    textAlign: "center",
    fontSize: "13px"
  }}
>
  <a href="/terms">Terms of Use</a>
  {" • "}
  <a href="/privacy">Privacy Policy</a>
</div>

<div
  style={{
    marginTop: "24px",
    textAlign: "center",
    fontSize: "13px",
    color: "#9ca3af"
  }}
>
  Secure ESG reporting • AI-powered workflows • CSRD-ready
</div>

{statusMessage && (
  <div
    style={{
      marginTop: "16px",
      padding: "12px",
      borderRadius: "10px",
      background: "#f8fafc",
      border: "1px solid #e5e7eb",
      color: "#374151",
      fontSize: "14px"
    }}
  >
    {statusMessage}
  </div>
)}
  </div>
</PageContainer>
  );
}

return (
  <PageContainer>
    <Navbar
      title="Ecovanta"
      subtitle="AI-powered ESG and CSRD reporting platform"
      rightContent={
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            alignItems: "center"
          }}
        >
          <Badge tone="success">
           Plan: {isUserLoaded ? currentPlanConfig.name : "Loading..."}
          </Badge>

          <Badge tone="default">
            Role: {user?.role || "preparer"}
          </Badge>

          {currentPlan === PLAN_KEYS.FREE && (
            <>
              <Button
                variant="primary"
                onClick={() =>
                  upgradePlan("pro", selectedBillingCycle)
                }
              >
                {getPlanConfig(PLAN_KEYS.PRO).cta}
              </Button>

              <Button
                variant="warning"
                onClick={() =>
                  upgradePlan("enterprise", selectedBillingCycle)
                }
              >
                {getPlanConfig(PLAN_KEYS.ENTERPRISE).cta}
              </Button>
            </>
          )}

          {isUserLoaded && (
  <>
    {currentPlan === PLAN_KEYS.ENTERPRISE && (
      <>
        <Button
          variant="primary"
          onClick={() =>
            changeSubscriptionPlan("pro", selectedBillingCycle)
          }
        >
          Downgrade to Pro
        </Button>

        <Button
          variant="secondary"
          onClick={() =>
            changeSubscriptionPlan("starter", selectedBillingCycle)
          }
        >
          Downgrade to Starter
        </Button>
      </>
    )}

    {currentPlan === PLAN_KEYS.PRO && (
      <>
        <Button
          variant="secondary"
          onClick={() =>
            changeSubscriptionPlan("starter", selectedBillingCycle)
          }
        >
          Downgrade to Starter
        </Button>

        <Button
          variant="warning"
          onClick={() =>
            upgradePlan("enterprise", selectedBillingCycle)
          }
        >
          Upgrade to Enterprise
        </Button>
      </>
    )}

    {currentPlan === PLAN_KEYS.STARTER && (
      <>
        <Button
          variant="primary"
          onClick={() =>
            upgradePlan("pro", selectedBillingCycle)
          }
        >
          Upgrade to Pro
        </Button>

        <Button
          variant="warning"
          onClick={() =>
            upgradePlan("enterprise", selectedBillingCycle)
          }
        >
          Upgrade to Enterprise
        </Button>
      </>
    )}
  </>
)}

          <Button
            variant="secondary"
            onClick={logout}
          >
            Logout
          </Button>
        </div>
      }
    />

  <SidebarLayout
    active={activePage}
    currentPlan={currentPlanConfig.name}
    userRole={user?.role || "preparer"}
    onNavigate={setActivePage}
  >
    {statusMessage && (
      <div
        style={{
          marginBottom: "20px",
          padding: "12px 16px",
          background: "#ffffff",
          borderRadius: "10px",
          border: "1px solid #d1d5db"
        }}
      >
        {statusMessage}
      </div>
    )}

    {activePage === "Dashboard" && (
      <ExecutiveDashboardPage
        analytics={analytics}
        reports={reports}
        auditLogs={auditLogs}
        user={user}
        onNavigate={setActivePage}
      />
    )}

{activePage === "Admin" && user?.role === "super_admin" && (
  <Section
    title="Admin Dashboard"
    subtitle="Manage users, companies, plans, subscriptions, and support cases."
  >
    <AdminDashboard
      API={API}
      authHeaders={authHeaders}
      setStatusMessage={setStatusMessage}
    />
  </Section>
)}

{activePage === "Subscription" && (
  <Section
    title="Billing & Subscription"
    subtitle="Manage plans, upgrades, enterprise features, and billing access."
  >
    <SubscriptionPage
      currentPlan={currentPlan}
      selectedBillingCycle={selectedBillingCycle}
      userBillingCycle={
        user?.billingCycle ||
        user?.subscription?.billingCycle ||
        user?.subscription?.interval ||
        "monthly"
      }
      hasStripeSubscription={Boolean(user?.stripeSubscriptionId)}

      monthlyCredits={user?.monthlyCredits ?? 0}
      creditsRemaining={user?.creditsRemaining ?? 0}
      reportsGeneratedThisMonth={
        user?.reportsGeneratedThisMonth ?? 0
      }
      maxReports={user?.maxReports ?? 0}

      onBillingCycleChange={updateSelectedBillingCycle}
      onUpgrade={upgradePlan}
      onChangeSubscriptionPlan={changeSubscriptionPlan}
      onManageBilling={openBillingPortal}
    />
  </Section>
)}

{activePage === "Evidence Library" && (
  <EvidenceLibraryPage />
)}

{activePage === "Document Intelligence" && (
  <DocumentIntelligencePage />
)}

{activePage === "Copilot" && (
  hasCopilotAccess ? (
    <CopilotWorkspacePage />
  ) : (
    <Section
      title="Upgrade to access Ecovanta Copilot"
      subtitle="Ecovanta Copilot is available on Pro and Enterprise plans."
    >
      <div
        style={{
          maxWidth: "760px",
          padding: "28px",
          background: "#ffffff",
          border: "1px solid #dbe4df",
          borderRadius: "18px"
        }}
      >
        <div
          aria-hidden="true"
          style={{
            fontSize: "34px",
            marginBottom: "14px"
          }}
        >
          ✨
        </div>

        <h2 style={{ margin: "0 0 10px" }}>
          Ecovanta Copilot requires Pro or Enterprise
        </h2>

        <p
          style={{
            margin: 0,
            color: "#596760",
            lineHeight: 1.7
          }}
        >
          Your current plan is {currentPlanConfig.name}. Upgrade to unlock
          grounded ESG assistance, evidence-aware answers, reporting support,
          and controlled workspace actions.
        </p>

        <div style={{ marginTop: "20px" }}>
          <Button
            onClick={() => setActivePage("Subscription")}
          >
            View upgrade options
          </Button>
        </div>
      </div>
    </Section>
  )
)}

{activePage === "Customer Dashboard" && (
  <CustomerDashboardPage />
)}

{activePage === "Leads CRM" && (
  <LeadManagementDashboard />
)}

{activePage === "Follow-Ups" && (
  <FollowUpDashboard />
)}





    {activePage === "AI Draft" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(380px, 520px) 1fr",
            gap: "20px",
            alignItems: "start"
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
            }}
           >
            <Section
              title="Create ESRS Report"
              subtitle="Prepare CSRD-compliant ESG disclosures."
            >
              
            </Section>
            <GuidedSteps step={currentStep} />

            <div
              style={{
                padding: "10px",
                borderRadius: "8px",
                background:
                  reportForm.reviewStatus === "draft"
                    ? "#e0f2fe"
                    : reportForm.reviewStatus === "in_review"
                    ? "#fef3c7"
                    : reportForm.reviewStatus === "approved"
                    ? "#d1fae5"
                    : "#ede9fe",
                marginBottom: "12px"
              }}
            >
              <strong>Status:</strong> {reportForm.reviewStatus}
            </div>

            {!isEditable() && (
              <div
                style={{
                  background: "#fee2e2",
                  padding: "10px",
                  borderRadius: "8px",
                  marginBottom: "12px"
                }}
              >
                This report is in review, approved, or published mode and
                cannot be edited.
              </div>
            )}

            <div style={{ display: "grid", gap: "12px" }}>
              <div>
                <FieldLabel helpKey="companyName">Company Name</FieldLabel>
                <Input
                  value={reportForm.companyName}
                  onChange={(e) => setTopField("companyName", e.target.value)}
                  placeholder="Company name"
                  disabled={!isEditable()}
                />
        </div>

<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    alignItems: "start"
  }}
>
  <div style={{ display: "flex", flexDirection: "column" }}>
    <FieldLabel helpKey="sector">Sector</FieldLabel>
  
<Select
  value={reportForm.sector}
  onChange={(e) => setTopField("sector", e.target.value)}
  disabled={!isEditable()}
  style={{
    marginTop: "4px"
  }}
>
        {/* Core sectors */}
      <option value="technology">Technology</option>
      <option value="energy">Energy</option>
      <option value="manufacturing">Manufacturing</option>
      <option value="finance">Finance</option>
      <option value="healthcare">Healthcare</option>
      <option value="retail">Retail</option>
      <option value="transport">Transport</option>
      <option value="construction">Construction</option>

      {/* Your extended sectors (KEEP THESE) */}
      <option value="agriculture-food">Agriculture & Food</option>
      <option value="telecommunications">Telecommunications</option>
      <option value="mining">Mining & Metals</option>
      <option value="chemicals">Chemicals</option>
      <option value="hospitality">Hospitality & Tourism</option>
      <option value="education">Education</option>
      <option value="public-sector">Public Sector</option>
      <option value="other">Other</option>
   </Select>
  </div>

  <div style={{ display: "flex", flexDirection: "column" }}>
    <FieldLabel helpKey="reportingYear">Reporting Year</FieldLabel>
  <Input
  type="number"
  value={reportForm.reportingYear}
  onChange={(e) =>
    setTopField("reportingYear", Number(e.target.value))
  }
  disabled={!isEditable()}
  placeholder="Reporting year"
  style={{
    marginTop: "4px"
  }}
/>
  </div>
</div>

              <h3
  style={{
    fontSize: "20px",
    fontWeight: "700",
    marginBottom: "14px",
    color: "#111827"
  }}
>
  ESRS 2
</h3>

              <div>
                <FieldLabel helpKey="governance">Governance</FieldLabel>
                <Textarea
                  value={reportForm.esrs2.governance}
                  onChange={(e) =>
                    setNestedField("esrs2", "governance", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Governance"
                  rows={3}
                />
              </div>

              <div>
                <FieldLabel helpKey="strategy">Strategy</FieldLabel>
                <Textarea
                  value={reportForm.esrs2.strategy}
                  onChange={(e) =>
                    setNestedField("esrs2", "strategy", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Strategy"
                  rows={3}
                />
              </div>

              <div>
                <FieldLabel helpKey="iro">
                  Impacts, Risks and Opportunities
                </FieldLabel>
                <textarea
                  value={reportForm.esrs2.impactsRisksOpportunities}
                  onChange={(e) =>
                    setNestedField(
                      "esrs2",
                      "impactsRisksOpportunities",
                      e.target.value
                    )
                  }
                  disabled={!isEditable()}
                  placeholder="Impacts, risks and opportunities"
                  rows={3}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <div>
                <FieldLabel helpKey="metricsTargets">
                  Metrics and Targets
                </FieldLabel>
                <textarea
                  value={reportForm.esrs2.metricsTargets}
                  onChange={(e) =>
                    setNestedField("esrs2", "metricsTargets", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Metrics and targets"
                  rows={3}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  marginBottom: "14px",
                  color: "#111827"
                }}
              >
                E1 - Climate
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px"
                }}
              >
                <div>
                  <FieldLabel helpKey="scope1">Scope 1 Emissions</FieldLabel>
             <Input
                type="number"
                value={reportForm.e1.scope1Emissions}
                onChange={(e) =>
                  setNestedField(
                    "e1",
                    "scope1Emissions",
                    Number(e.target.value)
                  )
                }
                disabled={!isEditable()}
                placeholder="Scope 1 emissions"
              />
                </div>

                <div>
                  <FieldLabel helpKey="scope2">Scope 2 Emissions</FieldLabel>
             <Input
                type="number"
                value={reportForm.e1.scope2Emissions}
                onChange={(e) =>
                  setNestedField(
                    "e1",
                    "scope2Emissions",
                    Number(e.target.value)
                  )
                }
                disabled={!isEditable()}
                placeholder="Scope 2 emissions"
              />
                </div>
              </div>

              <div>
                <FieldLabel helpKey="scope3">Scope 3 Emissions</FieldLabel>
                <Input
                type="number"
                value={reportForm.e1.scope3Emissions}
                onChange={(e) =>
                  setNestedField("e1", "scope3Emissions", Number(e.target.value))
                }
                disabled={!isEditable()}
                placeholder="Scope 3 emissions"
              />
              </div>

              <div>
                <FieldLabel helpKey="climatePolicies">Climate Policies</FieldLabel>
                <textarea
                  value={reportForm.e1.climatePolicies}
                  onChange={(e) =>
                    setNestedField("e1", "climatePolicies", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Climate policies"
                  rows={3}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <h3
  style={{
    fontSize: "20px",
    fontWeight: "700",
    marginBottom: "14px",
    color: "#111827"
  }}
>
  S1 - Own Workforce
</h3>

              <div>
                <FieldLabel helpKey="workforcePolicies">
                  Workforce Policies
                </FieldLabel>
                <textarea
                  value={reportForm.s1.workforcePolicies}
                  onChange={(e) =>
                    setNestedField("s1", "workforcePolicies", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Workforce policies"
                  rows={3}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <div>
                <FieldLabel helpKey="diversityInclusion">
                  Diversity and Inclusion
                </FieldLabel>
                <textarea
                  value={reportForm.s1.diversityInclusion}
                  onChange={(e) =>
                    setNestedField("s1", "diversityInclusion", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Diversity and inclusion"
                  rows={3}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <h3
  style={{
    fontSize: "20px",
    fontWeight: "700",
    marginBottom: "14px",
    color: "#111827"
  }}
>
  G1 - Business Conduct
</h3>

              <div>
                <FieldLabel helpKey="antiCorruption">Anti-corruption</FieldLabel>
                <textarea
                  value={reportForm.g1.antiCorruption}
                  onChange={(e) =>
                    setNestedField("g1", "antiCorruption", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Anti-corruption"
                  rows={3}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <div>
                <FieldLabel helpKey="whistleblowing">Whistleblowing</FieldLabel>
                <textarea
                  value={reportForm.g1.whistleblowing}
                  onChange={(e) =>
                    setNestedField("g1", "whistleblowing", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Whistleblowing"
                  rows={3}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

            <Section
              title="Double Materiality"
              subtitle="Assess ESG impact and financial materiality."
            >
              </Section>

              {reportForm.materialityTopics.map((topic, index) => {
                const scores = calculateMaterialityScores(topic);

                return (
                  <div
                    key={`topic-${index}`}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      padding: "14px",
                      background: "#fafafa"
                    }}
                  >
                    <div style={{ display: "grid", gap: "10px" }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 2fr",
                          gap: "10px"
                        }}
                      >
                        <div>
                          <label
                            style={{ fontWeight: "bold", marginBottom: "6px", display: "block" }}
                          >
                            Topic Code
                          </label>
                     <Input
                      value={topic.topicCode}
                      onChange={(e) =>
                        updateMaterialityTopic(
                          index,
                          "topicCode",
                          e.target.value
                        )
                      }
                      disabled={!isEditable()}
                      placeholder="Topic code"
                    />
                        </div>

                        <div>
                          <label
                            style={{ fontWeight: "bold", marginBottom: "6px", display: "block" }}
                          >
                            Topic Label
                          </label>
                          <Input
  value={topic.topicLabel}
  onChange={(e) =>
    updateMaterialityTopic(
      index,
      "topicLabel",
      e.target.value
    )
  }
  disabled={!isEditable()}
  placeholder="Topic label"
/>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(5, 1fr)",
                          gap: "8px"
                        }}
                      >
                        {[
                          ["severity", "Severity"],
                          ["scale", "Scale"],
                          ["scope", "Scope"],
                          ["irremediability", "Irrem."],
                          ["likelihood", "Impact Likelihood"]
                        ].map(([field, label]) => (
                          <div key={field}>
                            <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                              {label}
                            </div>
                            <Input
                            type="number"
                            min="1"
                            max="5"
                            value={topic.impactMateriality[field]}
                            onChange={(e) =>
                              updateMaterialityTopic(
                                index,
                                `impactMateriality.${field}`,
                                Number(e.target.value)
                              )
                            }
                            disabled={!isEditable()}
                          />
                          </div>
                        ))}
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: "8px"
                        }}
                      >
                        <div>
                          <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                            Magnitude
                          </div>
                          <Input
                          type="number"
                          min="1"
                          max="5"
                          value={topic.financialMateriality.magnitude}
                          onChange={(e) =>
                            updateMaterialityTopic(
                              index,
                              "financialMateriality.magnitude",
                              Number(e.target.value)
                            )
                          }
                          disabled={!isEditable()}
                        />
                        </div>

                        <div>
                          <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                            Likelihood
                          </div>
                          <Input
  type="number"
  min="1"
  max="5"
  value={topic.financialMateriality.likelihood}
  onChange={(e) =>
    updateMaterialityTopic(
      index,
      "financialMateriality.likelihood",
      Number(e.target.value)
    )
  }
  disabled={!isEditable()}
/>
                        </div>

                        <div>
                          <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                            Time horizon
                          </div>
                          <select
                            value={topic.financialMateriality.timeHorizon}
                            onChange={(e) =>
                              updateMaterialityTopic(
                                index,
                                "financialMateriality.timeHorizon",
                                e.target.value
                              )
                            }
                            disabled={!isEditable()}
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "10px",
                              border: "1px solid #d1d5db"
                            }}
                          >
                            <option value="short">Short</option>
                            <option value="medium">Medium</option>
                            <option value="long">Long</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <FieldLabel helpKey="stakeholders">
                          Stakeholders Consulted
                        </FieldLabel>
                            <Input
                            value={topic.stakeholdersConsulted}
                            onChange={(e) =>
                              updateMaterialityTopic(
                                index,
                                "stakeholdersConsulted",
                                e.target.value
                              )
                            }
                            disabled={!isEditable()}
                            placeholder="Stakeholders consulted (comma-separated)"
/>
                      </div>

                      <label style={{ fontSize: "14px", color: "#4b5563" }}>
                        <input
                          type="checkbox"
                          checked={!!topic.isMaterial}
                          onChange={(e) =>
                            updateMaterialityTopic(
                              index,
                              "isMaterial",
                              e.target.checked
                            )
                          }
                          disabled={!isEditable()}
                          style={{ marginRight: "8px" }}
                        />
                        Mark as material
                      </label>

                      <div>
                        <FieldLabel helpKey="rationale">Rationale</FieldLabel>
                        <textarea
                          value={topic.rationale}
                          onChange={(e) =>
                            updateMaterialityTopic(
                              index,
                              "rationale",
                              e.target.value
                            )
                          }
                          disabled={!isEditable()}
                          placeholder="Rationale"
                          rows={3}
                          style={{
                            padding: "10px",
                            borderRadius: "10px",
                            border: "1px solid #d1d5db",
                            width: "100%"
                          }}
                        />
                      </div>

                      <div
                        style={{
                          background: "#ffffff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                          padding: "12px"
                        }}
                      >
                        <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                          Materiality Score
                        </div>
                        <div>Impact Score: {scores.impactScore100}/100</div>
                        <div>
                          Financial Score: {scores.financialScore100}/100
                        </div>
                        <div>
                          Overall Score: {scores.overallMaterialityScore}/100
                        </div>
                        <div>
                          Result:{" "}
                          <strong
                            style={{
                              color: scores.isMaterial ? "#b91c1c" : "#166534"
                            }}
                          >
                            {scores.isMaterial ? "Material" : "Not Material"}
                          </strong>
                        </div>
                      </div>

                      {reportForm.materialityTopics.length > 1 && (
                        <button
                          onClick={() => removeMaterialityTopic(index)}
                          disabled={!isEditable()}
                          style={{
                            padding: "10px 12px",
                            borderRadius: "10px",
                            border: "1px solid #ef4444",
                            background: "#ffffff",
                            color: "#ef4444",
                            cursor: "pointer",
                            fontWeight: "bold",
                            opacity: !isEditable() ? 0.6 : 1
                          }}
                        >
                          Remove Topic
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              <button
                onClick={addMaterialityTopic}
                disabled={!isEditable()}
                style={{
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #d1d5db",
                  background: "#ffffff",
                  cursor: "pointer",
                  fontWeight: "bold",
                  opacity: !isEditable() ? 0.6 : 1
                }}
              >
                Add Material Topic
              </button>

             <Section
                title="AI Draft"
                subtitle="Generate AI-assisted sustainability reporting narratives."
              >
                </Section> 

           {hasAiDraftAccess ? (
                <button
                  onClick={generateAiDraft}
                  disabled={aiLoading || !isEditable()}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "none",
                    background: "#7c3aed",
                    color: "#ffffff",
                    fontWeight: "bold",
                    cursor:
                      aiLoading || !isEditable()
                        ? "not-allowed"
                        : "pointer",
                    opacity: aiLoading || !isEditable() ? 0.7 : 1
                  }}
                >
                  {aiLoading ? "Generating AI draft..." : "Generate AI Draft"}
                </button>
              ) : (
                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "10px",
                    background: "#fef3c7",
                    color: "#92400e",
                    fontWeight: "bold"
                  }}
                >
                  AI Draft is a Pro feature.
                </div>
              )}

              <div>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "6px"
                  }}
                >
                  Executive Summary — Powered by Ecovanta Intelligence
                </label>
                <textarea
                  value={reportForm.aiDraft.executiveSummary}
                  onChange={(e) =>
                    setNestedField("aiDraft", "executiveSummary", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="Executive Summary — Powered by Ecovanta Intelligence"
                  rows={4}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "6px"
                  }}
                >
                  AI Disclosure Draft
                </label>
                <textarea
                  value={reportForm.aiDraft.disclosureDraft}
                  onChange={(e) =>
                    setNestedField("aiDraft", "disclosureDraft", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="AI disclosure draft"
                  rows={8}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "6px"
                  }}
                >
                  AI Data Gaps
                </label>
                <textarea
                  value={reportForm.aiDraft.dataGaps}
                  onChange={(e) =>
                    setNestedField("aiDraft", "dataGaps", e.target.value)
                  }
                  disabled={!isEditable()}
                  placeholder="AI data gaps"
                  rows={4}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    width: "100%"
                  }}
                />
              </div>

              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "12px"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                    marginBottom: "14px"
                  }}
                >
                  <strong style={{ fontSize: "16px" }}>AI Recommendations</strong>

                  <select
                    value={recommendationFilter}
                    onChange={(e) => setRecommendationFilter(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "10px",
                      border: "1px solid #d1d5db",
                      background: "#ffffff"
                    }}
                  >
                    <option value="all">All</option>
                    <option value="high">High Priority</option>
                    <option value="compliance">Compliance</option>
                    <option value="strategy">Strategy</option>
                  </select>
                </div>

                {filteredRecommendations.length === 0 ? (
                  <div style={{ color: "#6b7280" }}>No AI recommendations</div>
                ) : (
                  <div style={{ display: "grid", gap: "14px" }}>
                    {filteredRecommendations.map((item) => {
                      const priorityStyle = getPriorityColor(item.priority);

                      return (
                        <div
                          key={item.id}
                          style={{
                            background: "#ffffff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            padding: "16px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "start",
                              gap: "12px",
                              flexWrap: "wrap",
                              marginBottom: "12px"
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  marginBottom: "6px"
                                }}
                              >
                                {item.title}
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  flexWrap: "wrap"
                                }}
                              >
                                {item.category && (
                                  <span
                                    style={{
                                      padding: "4px 10px",
                                      borderRadius: "999px",
                                      background: "#eef2ff",
                                      color: "#4338ca",
                                      fontSize: "12px",
                                      fontWeight: "bold"
                                    }}
                                  >
                                    {item.category}
                                  </span>
                                )}

                                {item.timeline && (
                                  <span
                                    style={{
                                      padding: "4px 10px",
                                      borderRadius: "999px",
                                      background: "#f3f4f6",
                                      color: "#374151",
                                      fontSize: "12px",
                                      fontWeight: "bold"
                                    }}
                                  >
                                    {item.timeline}
                                  </span>
                                )}

                                {item.esrsReference && (
                                  <span
                                    style={{
                                      padding: "4px 10px",
                                      borderRadius: "999px",
                                      background: "#ecfeff",
                                      color: "#0f766e",
                                      fontSize: "12px",
                                      fontWeight: "bold"
                                    }}
                                  >
                                    {item.esrsReference}
                                  </span>
                                )}
                              </div>
                            </div>

                            {item.priority && (
                              <span
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "999px",
                                  background: priorityStyle.bg,
                                  color: priorityStyle.text,
                                  border: `1px solid ${priorityStyle.border}`,
                                  fontSize: "12px",
                                  fontWeight: "bold"
                                }}
                              >
                                {item.priority} Priority
                              </span>
                            )}
                          </div>

                          <div style={{ display: "grid", gap: "12px" }}>
                            {item.currentGap && (
                              <div>
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    marginBottom: "4px",
                                    color: "#111827"
                                  }}
                                >
                                  Current Gap
                                </div>
                                <div style={{ color: "#4b5563" }}>
                                  {item.currentGap}
                                </div>
                              </div>
                            )}

                            {item.riskImpact && (
                              <div>
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    marginBottom: "4px",
                                    color: "#111827"
                                  }}
                                >
                                  Risk / Impact
                                </div>
                                <div style={{ color: "#4b5563" }}>
                                  {item.riskImpact}
                                </div>
                              </div>
                            )}

                            {Array.isArray(item.actions) &&
                              item.actions.length > 0 && (
                                <div>
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                      marginBottom: "6px",
                                      color: "#111827"
                                    }}
                                  >
                                    Recommended Actions
                                  </div>
                                  <ul
                                    style={{
                                      margin: 0,
                                      paddingLeft: "18px",
                                      color: "#4b5563"
                                    }}
                                  >
                                    {item.actions.map((action, idx) => (
                                      <li key={`${item.id}-action-${idx}`}>
                                        {action}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                            {Array.isArray(item.suggestedKPIs) &&
                              item.suggestedKPIs.length > 0 && (
                                <div>
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                      marginBottom: "6px",
                                      color: "#111827"
                                    }}
                                  >
                                    Suggested KPIs
                                  </div>
                                  <ul
                                    style={{
                                      margin: 0,
                                      paddingLeft: "18px",
                                      color: "#4b5563"
                                    }}
                                  >
                                    {item.suggestedKPIs.map((kpi, idx) => (
                                      <li key={`${item.id}-kpi-${idx}`}>{kpi}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                onClick={saveReport}
                disabled={loading || !isEditable()}
                style={{
                  marginTop: "8px",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#1976d2",
                  color: "#ffffff",
                  fontWeight: "bold",
                  cursor:
                    loading || !isEditable() ? "not-allowed" : "pointer",
                  opacity: loading || !isEditable() ? 0.7 : 1
                }}
              >
                {loading ? "Saving..." : "Save Report"}
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: "20px" }}>
            <div
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
              }}
            >
              <Section
              title="Portfolio Analytics"
              subtitle="Monitor ESG portfolio trends, scoring, and reporting performance."
            >
              </Section>

              {reports.length === 0 ? (
                <p style={{ color: "#6b7280" }}>
                  No reports yet. Create your first ESRS report to populate the
                  dashboard.
                </p>
              ) : (
                <div style={{ width: "100%", height: "320px" }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="company" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="score" fill="#1976d2" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
              }}
            >
            <Section
              title="Saved Reports"
              subtitle="Load, review, edit, and manage ESG reports."
            >
            </Section>
              <div style={{ display: "grid", gap: "12px" }}>
                <select
                  value={selectedReportId}
                  onChange={(e) => setSelectedReportId(e.target.value)}
                  style={{
                    padding: "12px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db"
                  }}
                >
                  <option value="">Select report</option>
                  {reports.map((report) => (
                    <option
                      key={report._id || report.id}
                      value={report._id || report.id}
                    >
                      {report.companyName} - {report.reportingYear}
                    </option>
                  ))}
                </select>
                <Input
                value={reviewerEmail}
                onChange={(e) => setReviewerEmail(e.target.value)}
                placeholder="Reviewer email"
                type="email"
              />

<Button onClick={assignReviewer}>
  Assign Reviewer & Notify
</Button>
                {selectedReportId && (
  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
   <Button
  onClick={() =>
    updateReportStatus(selectedReportId, "in_review")
  }
>
  Send to Review
</Button>

<Button
  onClick={() =>
    updateReportStatus(selectedReportId, "approved")
  }
>
  Approve
</Button>

<Button
  onClick={() =>
    updateReportStatus(selectedReportId, "published")
  }
>
  Publish
</Button>
  </div>
)}

                <button
                 onClick={() => {
                  const selectedReport = reports.find(
                    (report) => (report._id || report.id) === selectedReportId
                  );

                  loadReportIntoForm(selectedReport);
                }}
                  disabled={!selectedReportId}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    background: "#ffffff",
                    cursor: selectedReportId ? "pointer" : "not-allowed",
                    opacity: selectedReportId ? 1 : 0.6
                  }}
                >
                  Load Into Form
                </button>

                <button
                  onClick={async () => {
                    setLoading(true);
                    setStatusMessage("Refreshing dashboard...");

                    try {
                      await refreshDashboard();

                      if (selectedReportId) {
                        const refreshedReport = reports.find(
                          (report) => (report._id || report.id) === selectedReportId
                        );

                        if (refreshedReport) {
                          loadReportIntoForm(refreshedReport);
                        }
                      }

                      setStatusMessage("Dashboard refreshed.");
                    } catch (err) {
                      setStatusMessage(`Refresh failed: ${err.message}`);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "1px solid #d1d5db",
                    background: "#ffffff",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? "Refreshing..." : "Refresh Dashboard"}
                </button>
              </div>
            </div>
          </div>
        </div>


    )}

    {activePage === "Materiality" && (
      <div style={{ display: "grid", gap: "20px" }}>
        <Section
          title="Double Materiality"
          subtitle="Review materiality scores and the current impact/financial materiality heatmap."
        />

        <Card style={{ padding: "20px" }}>
          <p style={{ color: "#6b7280", marginTop: 0 }}>
            To edit materiality topics, open the AI Draft workspace. This page summarizes
            the currently selected report's materiality assessment.
          </p>

          {materialityHeatmapData.length === 0 ? (
            <p style={{ color: "#6b7280" }}>No materiality topics yet.</p>
          ) : (
            <div style={{ width: "100%", height: "320px" }}>
              <ResponsiveContainer>
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <CartesianGrid />
                  <XAxis
                    type="number"
                    dataKey="x"
                    name="Financial"
                    domain={[0, 100]}
                    label={{ value: "Financial Score", position: "insideBottom", offset: -8 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    name="Impact"
                    domain={[0, 100]}
                    label={{ value: "Impact Score", angle: -90, position: "insideLeft" }}
                  />
                  <ZAxis type="number" dataKey="z" range={[120]} />
                  <Tooltip />
                  <Scatter data={materialityHeatmapData} fill="#7c3aed" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    )}

      {activePage === "Analytics" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "20px",
              marginTop: "24px",
              marginBottom: "24px"
            }}
          >
            <div
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
              }}
            >
           <Section
              title="Benchmark Comparison"
              subtitle="Compare ESG reporting performance against sector benchmarks."
            >
              </Section>
              <p style={{ color: "#6b7280", marginTop: 0 }}>
                Compare current score against sector reference points.
              </p>

              <div style={{ width: "100%", height: "280px" }}>
      <ResponsiveContainer width="100%" height={320}>
  <BarChart
    data={benchmarkComparisonData}
    margin={{
      top: 10,
      right: 20,
      left: 0,
      bottom: 10
    }}
  >
    <CartesianGrid
      strokeDasharray="3 3"
      stroke="#e5e7eb"
    />

    <XAxis
      dataKey="name"
      tick={{
        fontSize: 12,
        fill: "#6b7280"
      }}
      axisLine={{
        stroke: "#d1d5db"
      }}
      tickLine={false}
    />

    <YAxis
      domain={[0, 100]}
      tick={{
        fontSize: 12,
        fill: "#6b7280"
      }}
      axisLine={{
        stroke: "#d1d5db"
      }}
      tickLine={false}
    />

    <Tooltip
      contentStyle={{
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        boxShadow:
          "0 10px 25px rgba(15,23,42,0.08)"
      }}
    />

    <Bar
      dataKey="value"
      radius={[10, 10, 0, 0]}
    >
      {benchmarkComparisonData.map(
        (entry, index) => (
          <Cell
            key={`bench-${index}`}
            fill={
              entry.name === "Your Company"
                ? "#1976d2"
                : entry.name === "Sector Average"
                ? "#f59e0b"
                : "#10b981"
            }
          />
        )
      )}
    </Bar>
  </BarChart>
</ResponsiveContainer>
              </div>
            </div>
            
            {hasAdvancedAnalyticsAccess && (
              <>
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "20px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
                  }}
                >
                  <Section
                    title="Materiality Heatmap"
                    subtitle="Visualize ESG impact and financial materiality priorities."
                  />

                  <p style={{ color: "#6b7280", marginTop: 0 }}>
                    Impact materiality vs financial materiality by topic.
                  </p>

                  {materialityHeatmapData.length === 0 ? (
                    <p style={{ color: "#6b7280" }}>No materiality topics yet.</p>
                  ) : (
                    <div style={{ width: "100%", height: "280px" }}>
                      <ResponsiveContainer>
                        <ScatterChart
                          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                        >
                          <CartesianGrid />
                          <XAxis
                            type="number"
                            dataKey="x"
                            name="Financial"
                            domain={[0, 100]}
                            label={{
                              value: "Financial Score",
                              position: "insideBottom",
                              offset: -8
                            }}
                          />
                          <YAxis
                            type="number"
                            dataKey="y"
                            name="Impact"
                            domain={[0, 100]}
                            label={{
                              value: "Impact Score",
                              angle: -90,
                              position: "insideLeft"
                            }}
                          />
                          <ZAxis type="number" dataKey="z" range={[120]} />
                          <Tooltip
                            cursor={{ strokeDasharray: "3 3" }}
                            content={({ active, payload }) => {
                              if (!active || !payload || !payload.length) {
                                return null;
                              }

                              const point = payload[0].payload;

                              return (
                                <div
                                  style={{
                                    background: "#fff",
                                    border: "1px solid #d1d5db",
                                    padding: "10px",
                                    borderRadius: "8px"
                                  }}
                                >
                                  <div style={{ fontWeight: "bold" }}>
                                    {point.name} - {point.label}
                                  </div>
                                  <div>Impact: {point.y}/100</div>
                                  <div>Financial: {point.x}/100</div>
                                  <div>Overall: {point.overall}/100</div>
                                  <div>Result: {point.result}</div>
                                </div>
                              );
                            }}
                          />
                          <Scatter data={materialityHeatmapData} fill="#7c3aed" />
                        </ScatterChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "20px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
                  }}
                >
                  <Section
                    title="Compliance Gap Dashboard"
                    subtitle="Track missing disclosures, ESG data gaps, and CSRD readiness."
                  />

                  <p style={{ color: "#6b7280", marginTop: 0 }}>
                    Tracks completeness by disclosure section and highlights missing
                    inputs.
                  </p>

                  <div style={{ display: "grid", gap: "12px" }}>
                    {complianceGapData.map((item) => (
                      <div
                        key={item.section}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                          padding: "14px",
                          background: "#fafafa"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "8px"
                          }}
                        >
                          <strong>{item.section}</strong>

                          <Badge
                            tone={
                              item.completeness >= 80
                                ? "approved"
                                : item.completeness >= 60
                                ? "review"
                                : "danger"
                            }
                          >
                            {item.completeness}%
                          </Badge>
                        </div>

                        <div
                          style={{
                            width: "100%",
                            height: "10px",
                            background: "#e5e7eb",
                            borderRadius: "999px",
                            overflow: "hidden",
                            marginBottom: "10px"
                          }}
                        >
                          <div
                            style={{
                              width: `${item.completeness}%`,
                              height: "100%",
                              background:
                                item.completeness >= 80
                                  ? "#10b981"
                                  : item.completeness >= 60
                                  ? "#f59e0b"
                                  : "#ef4444"
                            }}
                          />
                        </div>

                        {item.missing.length === 0 ? (
                          <div style={{ color: "#166534", fontSize: "14px" }}>
                            No major gaps identified.
                          </div>
                        ) : (
                          <div>
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginBottom: "6px"
                              }}
                            >
                              Missing:
                            </div>

                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: "18px",
                                color: "#6b7280"
                              }}
                            >
                              {item.missing.map((gap, idx) => (
                                <li key={`${item.section}-${idx}`}>{gap}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

    {activePage === "Reports" && (
      <div style={{ marginTop: "24px" }}>
<Section
  title="Portfolio Reports"
  subtitle="Overview of ESG reporting performance and activity."
>
  </Section>

  {reports.length === 0 ? (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        padding: "20px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
      }}
    >
      No reports generated yet.
    </div>
  ) : (
    reports.map((report) => {
      const score = Number(report.scorecard?.overallScore || 0);
      const reportRecommendations = parseRecommendationsText(
        report.aiDraft?.recommendations || ""
      );

      return (
        <div
          key={report._id || report.id}
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "16px",
            borderLeft: `8px solid ${getRiskColor(score)}`,
            boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              gap: "16px",
              flexWrap: "wrap"
            }}
          >
            <div>
              <h3 style={{ marginTop: 0, marginBottom: "8px" }}>
                {report.companyName}
              </h3>

              <div style={{ color: "#6b7280", marginBottom: "8px" }}>
                Sector: {report.sector} | Year: {report.reportingYear}
              </div>
            </div>

<div
  style={{
    padding: "8px 12px",
    borderRadius: "999px",
    background: getRiskColor(score),
    color: "#ffffff",
    fontWeight: "bold"
  }}
>
  {score} — {getRiskLabel(score)}
</div>

{/* 👇 CLOSE ONLY ONE parent container ABOVE (DO NOT ADD EXTRA) */}
</div>

{/* 👇 Benchmark + Status block */}
<div style={{ marginTop: "10px", marginBottom: "10px" }}>
  <strong>Benchmark:</strong> {report.scorecard?.benchmark || 0}

  <span style={{ marginLeft: 16 }}>
    <strong>Status:</strong>{" "}
    <span
      style={{
        padding: "4px 10px",
        borderRadius: "999px",
        background:
          report.reviewStatus === "draft"
            ? "#e0f2fe"
            : report.reviewStatus === "in_review"
            ? "#fef3c7"
            : report.reviewStatus === "approved"
            ? "#d1fae5"
            : report.reviewStatus === "published"
            ? "#ede9fe"
            : "#e5e7eb",
        color:
          report.reviewStatus === "draft"
            ? "#075985"
            : report.reviewStatus === "in_review"
            ? "#92400e"
            : report.reviewStatus === "approved"
            ? "#166534"
            : report.reviewStatus === "published"
            ? "#5b21b6"
            : "#374151",
        fontWeight: "bold"
      }}
    >
      {report.reviewStatus === "in_review"
        ? "In Review"
        : report.reviewStatus
        ? report.reviewStatus.charAt(0).toUpperCase() +
          report.reviewStatus.slice(1)
        : "Draft"}
    </span>
  </span>
</div>

          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "14px",
              whiteSpace: "pre-wrap",
              marginBottom: "12px"
            }}
          >
            <strong>Executive Summary — Powered by Ecovanta Intelligence</strong>
            <div style={{ marginTop: "8px" }}>
              {report.aiDraft?.executiveSummary || "No AI summary"}
            </div>
          </div>

          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "14px",
              marginBottom: "12px"
            }}
          >
            <strong>AI Recommendations</strong>

            <div style={{ marginTop: "10px", display: "grid", gap: "12px" }}>
              {reportRecommendations.length === 0 ? (
                <div style={{ color: "#6b7280" }}>No AI recommendations</div>
              ) : (
                reportRecommendations.map((item) => {
                  const priorityStyle = getPriorityColor(item.priority);

                  return (
                   <Card
                    key={item.id}
                    style={{
                    padding: "14px"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "start",
                          gap: "10px",
                          flexWrap: "wrap",
                          marginBottom: "8px"
                        }}
                      >
                        <div style={{ fontWeight: "bold" }}>{item.title}</div>

                        {item.priority && (
                          <span
                            style={{
                              padding: "5px 10px",
                              borderRadius: "999px",
                              background: priorityStyle.bg,
                              color: priorityStyle.text,
                              border: `1px solid ${priorityStyle.border}`,
                              fontSize: "12px",
                              fontWeight: "bold"
                            }}
                          >
                            {item.priority}
                          </span>
                        )}
                      </div>

                      <div style={{ color: "#6b7280", marginBottom: "6px" }}>
                        {[item.category, item.timeline, item.esrsReference]
                          .filter(Boolean)
                          .join(" • ")}
                      </div>

                      {item.currentGap && (
                        <div style={{ marginBottom: "8px" }}>
                          <strong>Gap:</strong> {item.currentGap}
                        </div>
                      )}

                      {Array.isArray(item.actions) && item.actions.length > 0 && (
                        <div>
                          <strong>Actions:</strong>
                          <ul style={{ marginBottom: 0, paddingLeft: "18px" }}>
                            {item.actions.map((action, idx) => (
                              <li key={`${item.id}-report-action-${idx}`}>
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          </div>


              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
  {hasPdfExportAccess ? (
   <Button variant="primary" onClick={() => downloadSingleReportPDF(report)}>
  Download This Report
</Button>
  ) : (
    <button disabled>PDF Export (Pro)</button>
  )}

 <Button variant="secondary" onClick={() => loadReportIntoForm(report._id || report.id)}>
  Edit In Form
</Button>

  <Button variant="danger" onClick={() => deleteReport(report)}>
  Delete
</Button>

{(report._id || report.id) && (
  <>
    <Button
      onClick={() =>
        updateReportStatus(report._id || report.id, "in_review")
      }
    >
      Send to Review
    </Button>

    <Button
      onClick={() =>
        updateReportStatus(report._id || report.id, "approved")
      }
    >
      Approve
    </Button>

    <Button
      onClick={() =>
        updateReportStatus(report._id || report.id, "published")
      }
    >
      Publish
    </Button>
  </>
)}

  {hasWorkflowAccess &&
  ["draft", "in_review"].includes(report.reviewStatus) &&
  ["admin", "approver"].includes(user?.role) && (
    <div
      style={{
        display: "flex",
        gap: "8px",
        flexWrap: "wrap",
        alignItems: "center"
      }}
    >
     <Input
        type="email"
        placeholder={
          report.assignedReviewerEmail
            ? `Assigned: ${report.assignedReviewerEmail}`
            : "Reviewer email"
        }
        value={reviewerEmailByReport[report._id || report.id] || ""}
        onChange={(e) =>
          setReviewerEmailByReport((prev) => ({
            ...prev,
            [report._id || report.id]: e.target.value
          }))
        }
        style={{
          minWidth: "220px"
        }}
      /> 

<Button
  variant="warning"
  onClick={() => assignReviewer(report)}
>
  {report.assignedReviewerEmail
    ? "Reassign Reviewer"
    : "Assign Reviewer"}
</Button>

      {!report.assignedReviewerEmail && report.reviewStatus === "draft" && (
        <button
          onClick={() =>
            updateReportStatus(report._id || report.id, "in_review")
          }
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            border: "1px solid #f59e0b",
            background: "#ffffff",
            color: "#92400e",
            fontWeight: "bold",
            cursor: "pointer"
          }}
        >
          Send Without Assignment
        </button>
      )}
    </div>
  )}

  {hasWorkflowAccess &&
    report.reviewStatus === "in_review" &&
    ["reviewer", "approver", "admin"].includes(user?.role) && (
      <button
        onClick={() =>
          updateReportStatus(report._id || report.id, "approved")
        }
      >
        Approve
      </button>
    )}

  {hasWorkflowAccess &&
    report.reviewStatus === "approved" &&
    ["approver", "admin"].includes(user?.role) && (
      <button
        onClick={() =>
          updateReportStatus(report._id || report.id, "published")
        }
      >
        Publish
      </button>
    )}
</div>


        </div>
      );
    })
  )}
</div>
    )}



    </SidebarLayout>
  </PageContainer>
);
}

function App() {
  const token = localStorage.getItem("token");
    const params = new URLSearchParams(
    window.location.search
  );

  const hasResetToken =
    params.get("resetToken") &&
    params.get("email");
  const showSignup =
  params.get("signup") === "true";
  const currentPath =
    window.location.pathname;

  // ======================================
  // LEGAL PAGES
  // ======================================

  if (currentPath === "/terms") {
    return <TermsPage />;
  }

  if (currentPath === "/privacy") {
    return <PrivacyPage />;
  }

//===========================================
//ESG MATURITY BENCHMARK
//==============================================

if (window.location.pathname === "/esg-maturity-benchmark") {
  return <ESGMaturityBenchmark />;
}
//==================================
//ESG MATURITY HISTORY
//========================================
if (window.location.pathname.startsWith("/esg-maturity-history/")) {
  return <ESGMaturityAssessmentDetail />;
}

if (window.location.pathname === "/esg-maturity-history") {
  return <ESGMaturityAssessmentHistory />;
}
//================================
// GAP ANALYSIS
//==================================

if (window.location.pathname === "/esg-gap-analysis") {
  return <ESGGapAnalysis />;
}
//==========================================
//REQUEST ACCESS
//========================================
if (window.location.pathname === "/request-access") {
  return <RequestAccessPage />;
}

//======================================
//ESG ROADMAP
//=========================================
if (window.location.pathname === "/esg-roadmap") {
  return <ESGRoadmapPreview />;
}
//====================================
// LEADS MANAGEMENT DASHBOARD
//===========================================

if (window.location.pathname === "/admin/leads") {
  return  (
  <UserProvider> 
    <LeadManagementDashboard />
  </UserProvider>
);
}
//==========================================
//FOLLOW UP DASHBOARD
//======================================
if (window.location.pathname === "/admin/follow-ups") {
  return  (
  <UserProvider>
  <FollowUpDashboard />
    </UserProvider>
);
}
//=====================================
//  UPLOAD DOCUMENTS
//====================================

if (window.location.pathname === "/admin/advisory-documents") {
  return (
  <UserProvider> 
    <AdvisoryDocumentsPage />
     </UserProvider>
);
}
//======================================
// CUSTOMER DASHBOARD
//========================================
if (window.location.pathname === "/app/dashboard") {
  return (
  <UserProvider>
  <CustomerDashboardPage />
  </UserProvider>
);
}

if (window.location.pathname === "/app/upgrade") {
  return (
    <UserProvider>
      <UpgradePage />
    </UserProvider>
  );
}

// ======================================
  // LANDING PAGE
  // ======================================

 if (!token && !hasResetToken && !showSignup) {
  return <EcovantaLandingPage />;
}

  // ======================================
  // MAIN PLATFORM
  // ======================================

return <MainPlatform />;
}

export default App;